import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Card, CardContent, Checkbox, Divider, FormControl,
  FormControlLabel, InputLabel, MenuItem, Select, Stack, TextField, Typography,
  ToggleButton, ToggleButtonGroup, Chip, Tooltip, Switch, Menu, ListItemIcon, ListItemText, IconButton,
  Dialog, DialogActions, DialogContent, DialogTitle, Radio, RadioGroup, Autocomplete, Slider
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import Papa from 'papaparse';
import { toast } from 'react-toastify';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DownloadIcon from '@mui/icons-material/Download';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import BlockIcon from '@mui/icons-material/Block';
import UndoIcon from '@mui/icons-material/Undo';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useNavigate } from 'react-router-dom';
import api from '../Api'; // axios instance
import { filterExcludedSkuRows, isSkuExcludedByPrefix } from '../utils/skuExclusions';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport
} from '@mui/x-data-grid';



// ===== Config =====
const REQUIRED_HEADERS = ['SKU','A/C','BRAND','STYLE','CMB','PRINT','COLOR','STYLE2','PRINT2','COLOR2','SIZE'];
const FLIPKART_INVENTORY_HEADERS = ['listing_id','fsn','sku','stock_quantity'];
const FLIPKART_LISTING_HEADERS = ['Seller SKU Id','Flipkart Serial Number','Listing ID','Listing Status'];
const MYNTRA_ARTICLE_HEADERS = ['ARTICLE NO','STYLE ID'];
const SAMPLE_HEADERS = ['SKU','FSN','A/C','BRAND','STYLE','CMB','FRT/BCK','PRINT','COLOR','STYLE2','FRT/BCK1','PRINT2','COLOR2','SIZE'];
const GLOBAL_CHUNK_SIZE = 5000;       // normal full-detail chunk size
const LITE_CHUNK_SIZE = 25000;        // lightweight load-all chunks
const UNLIMITED_CONCURRENCY = 4;      // parallel page fetchers
const SERVER_PAGE_SIZE_OPTIONS = [25, 50, 100];
const MAX_AUTOSIZE_WIDTH = 480;
const MIN_AUTOSIZE_WIDTH = 80;
const LS_KEY = 'sku_admin_settings_v5';
const NullColumnMenu = () => null;
const NullColumnMenuIcon = () => null;
const UNIQUE_VALUE_LIMIT = 1000; // stop after this many distinct values per column
const LOCAL_HEADER_HEIGHT = 100;
const LOCAL_SELECTION_COLUMN_WIDTH = 52;
const LOCAL_VIRTUAL_OVERSCAN = 8;
const IMAGE_FETCH_CHUNK_SIZE = 100;
const IMAGE_FETCH_CONCURRENCY = 3;
const LOCAL_IMAGE_LOOKUP_CONCURRENCY = 7;
const IMAGE_VIEW_MIN = 36;
const IMAGE_VIEW_MAX = 180;
const IMAGE_VIEW_STEP = 8;
const DEFAULT_IMAGE_VIEW_SIZE = 52;
const IMAGE_VIEW_PRESETS = [52, 84, 116, 148];
const LOCAL_FLIPKART_BRIDGE = 'http://127.0.0.1:8766';




// New: split fields generated from sku
const SPLIT_FIELDS = ['a','b','c','d','e','f','g','h','i','j','k','l','m','n','o'];
const MSKU_COMPONENT_FIELDS = ['brand','style','cmb','frt_bck','print1','color1','style2','frt_bck1','print2','color2','size'];

// Per-filter keys (for show/hide toggles)
const GRID_FIELD_KEYS = ['sku','fsn','image_url','variant_count','stock_quantity','real_skus','account_name','brand','style','cmb','frt_bck','print1','color1','style2','frt_bck1','print2','color2','size', ...SPLIT_FIELDS, 'm_sku_text', 'remarks'];
const FILTER_KEYS = GRID_FIELD_KEYS;
const CLIENT_ONLY_FILTER_FIELDS = new Set(['real_skus']);
const FIELD_LABELS = {
  a: 'A',
  sku: 'SKU',
  fsn: 'FSN',
  image_url: 'IMAGE',
  variant_count: 'VAR',
  stock_quantity: 'STOCK',
  real_skus: 'REAL SKU',
  account_name: 'A/C',
  brand: 'BRAND',
  style: 'STYLE',
  cmb: 'CMB',
  frt_bck: 'FRT/BCK',
  print1: 'PRINT',
  color1: 'COLOR',
  style2: 'STYLE2',
  frt_bck1: 'FRT/BCK1',
  print2: 'PRINT2',
  color2: 'COLOR2',
  size: 'SIZE',
  m_sku_text: 'M.SKU',
  remarks: 'REMARKS',
};

const fieldLabel = (field) => FIELD_LABELS[field] || String(field || '').toUpperCase();

const headerKey = (value) => safeStr(value).trim().toLowerCase();
const hasHeaders = (headers = [], required = []) => {
  const set = new Set(headers.map(headerKey));
  return required.every(header => set.has(headerKey(header)));
};
const detectUploadKind = (headers = []) => {
  if (hasHeaders(headers, FLIPKART_LISTING_HEADERS)) return 'flipkart_listing';
  if (hasHeaders(headers, FLIPKART_INVENTORY_HEADERS)) return 'flipkart_inventory';
  if (hasHeaders(headers, MYNTRA_ARTICLE_HEADERS)) return 'myntra_article';
  if (hasHeaders(headers, REQUIRED_HEADERS)) return 'sku_master';
  return '';
};
const rowValueAny = (row = {}, names = []) => {
  const wanted = new Set(names.map(headerKey));
  for (const [key, value] of Object.entries(row || {})) {
    if (wanted.has(headerKey(key))) return value;
  }
  return '';
};
const splitRealSkuSize = (value) => {
  const text = safeStr(value).trim();
  if (!text) return { baseSku: '', sizeSuffix: '' };
  const match = text.match(/^(.*?)(?:[\s_-]*)?(2XL|XXL|XL|S|M|L)\s*$/i);
  if (!match) return { baseSku: text, sizeSuffix: '' };
  const baseSku = safeStr(match[1]).replace(/[\s_-]+$/g, '').trim();
  return { baseSku: baseSku || text, sizeSuffix: safeStr(match[2]).toUpperCase() };
};
const normalizeFlipkartPreviewRow = (row = {}, accountName = '') => {
  const realSku = safeStr(rowValueAny(row, ['sku']));
  const { baseSku, sizeSuffix } = splitRealSkuSize(realSku);
  const fsn = safeStr(rowValueAny(row, ['fsn']));
  const stock = safeStr(rowValueAny(row, ['stock_quantity']));
  return {
    id: `preview-${fsn || realSku || Math.random()}`,
    sku: baseSku,
    fsn,
    image_url: '',
    variant_count: sizeSuffix ? 1 : '',
    stock_quantity: stock,
    real_skus: realSku ? `${sizeSuffix || '-'}:${realSku}${fsn ? ` (${fsn})` : ''}` : '',
    account_name: accountName,
    brand: '',
    style: '',
    cmb: '',
    frt_bck: '',
    print1: '',
    color1: '',
    style2: '',
    frt_bck1: '',
    print2: '',
    color2: '',
    size: sizeSuffix,
    m_sku_text: '',
    remarks: '',
  };
};

const normalizeFlipkartListingPreviewRow = (row = {}, accountName = '') => {
  const realSku = safeStr(rowValueAny(row, ['Seller SKU Id', 'seller sku id', 'seller_sku_id']));
  const { baseSku, sizeSuffix } = splitRealSkuSize(realSku);
  const fsn = safeStr(rowValueAny(row, ['Flipkart Serial Number', 'flipkart serial number', 'fsn']));
  return {
    id: `preview-${fsn || realSku || Math.random()}`,
    sku: baseSku,
    fsn,
    image_url: '',
    variant_count: sizeSuffix ? 1 : '',
    stock_quantity: safeStr(rowValueAny(row, ['Your Stock Count', 'System Stock count', 'stock_quantity'])),
    real_skus: realSku ? `${sizeSuffix || '-'}:${realSku}${fsn ? ` (${fsn})` : ''}` : '',
    account_name: accountName,
    size: sizeSuffix,
  };
};

const normalizeMyntraArticlePreviewRow = (row = {}, accountName = '') => ({
  id: `preview-${rowValueAny(row, ['STYLE ID']) || rowValueAny(row, ['ARTICLE NO']) || Math.random()}`,
  sku: safeStr(rowValueAny(row, ['ARTICLE NO'])),
  fsn: safeStr(rowValueAny(row, ['STYLE ID'])),
  image_url: '',
  variant_count: '',
  stock_quantity: '',
  real_skus: '',
  account_name: accountName,
  brand: safeStr(rowValueAny(row, ['BRAND NAME'])),
  style: '',
  cmb: '',
  frt_bck: '',
  print1: '',
  color1: safeStr(rowValueAny(row, ['COLOR'])),
  style2: '',
  frt_bck1: '',
  print2: '',
  color2: '',
  size: '',
  m_sku_text: '',
  remarks: '',
});

const accountGroupFromName = (name) => {
  const text = safeStr(name).trim().toUpperCase();
  if (!text) return '';
  if (text.startsWith('FK ')) return 'FK';
  if (text.startsWith('MYS ') || text.startsWith('MYE ') || text === 'MY' || text.startsWith('MY ')) return 'MY';
  return text.split(/\s+/)[0] || '';
};

const accountSubgroupFromName = (name) => {
  const text = safeStr(name).trim();
  if (!text) return '';
  return text.replace(/^(FK|MYS|MYE|MY)\s+/i, '').trim() || text;
};

const accountDisplayName = (account) => {
  const name = safeStr(account?.name || account);
  const group = accountGroupFromName(name);
  const subgroup = accountSubgroupFromName(name);
  return group && subgroup && subgroup !== name ? `${subgroup} (${name})` : name;
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// ===== Helpers =====
const safeStr = (v) => (v === null || v === undefined) ? '' : String(v);

function highlightHTML(value, query) {
  const text = safeStr(value);
  const q = safeStr(query).trim();
  if (!q) return text;
  try {
    const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')})`, 'ig');
    return text.replace(re, '<mark>$1</mark>');
  } catch { return text; }
}

function highlightTokens(value, tokens = []) {
  let html = safeStr(value);
  tokens
    .map(t => safeStr(t).trim())
    .filter(Boolean)
    .forEach(token => {
      html = highlightHTML(html, token);
    });
  return html;
}


function makeRenderCell({ filterScope, localQuery, globalFilters, localHeaderFilters }) {
  return (params) => {
    const field = params.field;
    const raw = params.value;
    let html = safeStr(raw);

    // Helper to highlight comma lists
    const highlightBy = (val) => {
      const q = safeStr(val).trim();
      if (!q || q === '?') return;
      const tokens = q.includes(',') ? splitCSV(q) : [q];
      tokens.forEach(t => { html = highlightHTML(html, t); });
    };

    if (filterScope === 'local') {
      // 1) Per-column LOCAL header filter
      highlightBy(localHeaderFilters?.[field]);

      // 2) ALSO: when user types in the GLOBAL SKU box, highlight SKU column in LOCAL mode
      if (field === 'sku') {
        highlightBy(globalFilters?.sku);
      }

      // 3) Local quick search across all columns
      highlightBy(localQuery);

    } else if (filterScope === 'global') {
      // Existing: per-field GLOBAL filter (skip '?' blanks)
      highlightBy(globalFilters?.[field]);
    }

    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  };
}


// --- Multi-value helpers ---
function splitCSV(value) {
  if (value === null || value === undefined) return [];
  return String(value)
    .replace(/\u200B/g, '')            // remove zero-width
    .split(/[,\uFF0C;|]+/)            // comma, fullwidth comma, semicolon, pipe
    .map(s => s.trim()
                 .replace(/[–—−‒―]/g, '-') // normalize various dash chars to '-'
           )
    .filter(Boolean);
}

const normalizeBreakWord = (value) => safeStr(value)
  .trim()
  .replace(/^[([{]+|[)\]}]+$/g, '')
  .replace(/[.:]+$/g, '')
  .toUpperCase();

const isBreakComboMarker = (value) => ['CMB', 'COMBO', 'COMB'].includes(normalizeBreakWord(value));
const isBreakConnector = (value) => ['&', '+', 'AND'].includes(normalizeBreakWord(value));
const isBreakColorPrefix = (value) => ['LGT', 'LT', 'LIGHT', 'DARK', 'DK'].includes(normalizeBreakWord(value));
const isNumericLeadWord = (value) => /^\d+[A-Z0-9_-]*$/i.test(normalizeBreakWord(value));
const isSkuCodeWord = (value) => /^[A-Z]+[_-]?\d+[A-Z0-9_-]*$/i.test(normalizeBreakWord(value));

function splitBreakWords(value) {
  return safeStr(value)
    .replace(/\s*([&+])\s*/g, ' $1 ')
    .replace(/[|,]+/g, ' ')
    .replace(/\//g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(part => part.trim())
    .filter(Boolean);
}

function splitBreakConnectorGroups(words) {
  const groups = [];
  let current = [];
  words.forEach((word) => {
    if (isBreakConnector(word)) {
      if (current.length) groups.push(current);
      current = [];
      return;
    }
    current.push(word);
  });
  if (current.length) groups.push(current);
  return groups;
}

function splitBreakTailGroups(words) {
  const cleanWords = (words || []).filter(Boolean);
  if (!cleanWords.length) return [];

  const connectorGroups = splitBreakConnectorGroups(cleanWords);
  if (connectorGroups.length > 1) {
      return connectorGroups.flatMap(group => splitBreakTailGroups(group));
  }

  if (cleanWords.some(isNumericLeadWord)) {
    const out = [];
    let pending = [];
    const flushPending = () => {
      if (pending.length) out.push(pending.join(' '));
      pending = [];
    };

    for (let i = 0; i < cleanWords.length; i += 1) {
      const word = cleanWords[i];
      if (!isNumericLeadWord(word)) {
        pending.push(word);
        continue;
      }

      flushPending();
      const group = [word];
      i += 1;
      while (i < cleanWords.length && !isNumericLeadWord(cleanWords[i]) && !isBreakConnector(cleanWords[i]) && !isBreakComboMarker(cleanWords[i])) {
        group.push(cleanWords[i]);
        i += 1;
      }
      i -= 1;
      out.push(group.join(' '));
    }
    flushPending();
    return out;
  }

  if (cleanWords.length <= 3 && cleanWords.some(isBreakColorPrefix)) {
    return [cleanWords.join(' ')];
  }

  const groups = [];
  for (let i = 0; i < cleanWords.length; i += 1) {
    const group = [cleanWords[i]];
    if (isBreakColorPrefix(cleanWords[i]) && i + 1 < cleanWords.length) {
      i += 1;
      group.push(cleanWords[i]);
    }
    if (i + 1 < cleanWords.length) {
      i += 1;
      group.push(cleanWords[i]);
    }
    groups.push(group.join(' '));
  }

  return groups;
}

function splitBreakBaseAndTail(words) {
  if (!words.length) return { base: '', tail: [] };
  const first = words[0];
  if (isBreakComboMarker(first)) return { base: '', tail: words };
  if (isSkuCodeWord(first) || /^\d+[A-Z0-9_-]*$/i.test(normalizeBreakWord(first))) {
    return { base: first, tail: words.slice(1) };
  }
  return { base: '', tail: words };
}

function splitOutsideSkuChunk(value) {
  const words = splitBreakWords(value);
  if (!words.length) return [];

  const markerIndex = words.findIndex(word => isBreakComboMarker(word));
  if (markerIndex >= 0) {
    const marker = words[markerIndex];
    const { base, tail: prefixTail } = splitBreakBaseAndTail(words.slice(0, markerIndex));
    const tail = words.slice(markerIndex + 1);
    return [
      base,
      marker,
      ...splitBreakTailGroups([...prefixTail, ...tail]),
    ];
  }

  const { base, tail } = splitBreakBaseAndTail(words);
  return [base, '', ...splitBreakTailGroups(tail)];
}

function splitBracketGroupsAndOutside(value) {
  const text = safeStr(value);
  const groups = [];
  let outside = '';

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch !== '(') {
      outside += ch;
      continue;
    }

    outside += ' ';
    let depth = 0;
    let group = '';
    for (; i < text.length; i += 1) {
      const groupCh = text[i];
      group += groupCh;
      if (groupCh === '(') depth += 1;
      if (groupCh === ')') {
        depth -= 1;
        if (depth === 0) break;
      }
    }
    if (group.trim()) groups.push(group.trim());
  }

  return { outside, groups };
}

function splitAutoSkuForBreak(value) {
  const { outside, groups } = splitBracketGroupsAndOutside(value);
  const [base = '', marker = '', ...outsideDesigns] = splitOutsideSkuChunk(outside);
  const designs = groups.length ? groups : outsideDesigns.filter(Boolean);
  return [base, marker, ...designs].slice(0, SPLIT_FIELDS.length);
}

function splitSkuForBreak(value, criteria = 'AUTO') {
  const text = safeStr(value).trim();
  if (!text) return [];

  const rule = safeStr(criteria).trim() || 'AUTO';

  if (/^regex:/i.test(rule)) {
    const pattern = rule.replace(/^regex:/i, '').trim();
    if (!pattern) return [text];
    try {
      const match = text.match(new RegExp(pattern));
      if (match) return match.slice(1).map(part => safeStr(part).trim()).filter(Boolean);
    } catch {
      return [text];
    }
    return [text];
  }

  if (/^delimiter:/i.test(rule)) {
    const delimiter = rule.replace(/^delimiter:/i, '');
    if (!delimiter) return [text];
    return text.split(delimiter).map(part => part.trim()).filter(Boolean);
  }

  if (/^legacy$/i.test(rule)) {
    return text.split(/[\s/_-]+/).map(part => part.trim()).filter(Boolean);
  }

  return splitAutoSkuForBreak(text);
}

function splitPartsToRowPatch(parts = []) {
  return SPLIT_FIELDS.reduce((acc, field, index) => {
    acc[field] = safeStr(parts[index]).trim();
    return acc;
  }, {});
}

/**
 * Normalize filters into:
 * - scalar: normal single string values (no comma)
 * - multi:  arrays for fields that had comma lists
 */
function normalizeFilters(filters) {
  const scalar = {};
  const multi = {};
  for (const [k, v] of Object.entries(filters || {})) {
    const txt = safeStr(v).trim();
    if (!txt) { scalar[k] = ''; continue; }
    if (txt.includes(',')) {
      multi[k] = splitCSV(txt);
    } else {
      scalar[k] = txt;
    }
  }
  return { scalar, multi };
}

function hasActiveFilters(filters = {}) {
  return Object.values(filters || {}).some(v => safeStr(v).trim().length > 0);
}

function clampServerPageSize(value) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n) || n < 1) return 100;
  return Math.min(n, 100);
}

function clampImageViewSize(value) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n)) return DEFAULT_IMAGE_VIEW_SIZE;
  return Math.max(IMAGE_VIEW_MIN, Math.min(IMAGE_VIEW_MAX, n));
}

function densityRowHeight(density) {
  if (density === 'compact') return 40;
  if (density === 'comfortable') return 64;
  return 52;
}

function imageRowHeight(density, imageViewSize) {
  return Math.max(densityRowHeight(density), clampImageViewSize(imageViewSize) + 12);
}

function imageColumnWidth(imageViewSize) {
  return clampImageViewSize(imageViewSize) + 34;
}

function compareGridValues(a, b) {
  const av = safeStr(a).trim();
  const bv = safeStr(b).trim();
  const an = Number(av.replace(/,/g, ''));
  const bn = Number(bv.replace(/,/g, ''));
  if (av && bv && Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
  return av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
}

function parseFilterExpression(value) {
  const tokens = splitCSV(value);
  const parsed = {
    include: [],
    exclude: [],
    includeExact: [],
    excludeExact: [],
    includeBlank: false,
    requireNonBlank: false,
  };

  tokens.forEach((rawToken) => {
    const token = safeStr(rawToken).trim();
    if (!token) return;
    if (token === '?') {
      parsed.includeBlank = true;
      return;
    }
    if (token === '!?') {
      parsed.requireNonBlank = true;
      return;
    }
    if (token.startsWith('!=')) {
      const excludedExact = token.slice(2).trim();
      if (excludedExact) parsed.excludeExact.push(excludedExact);
      return;
    }
    if (token.startsWith('!')) {
      const excluded = token.slice(1).trim();
      if (excluded) parsed.exclude.push(excluded);
      return;
    }
    if (token.startsWith('=')) {
      const includedExact = token.slice(1).trim();
      if (includedExact) parsed.includeExact.push(includedExact);
      return;
    }
    parsed.include.push(token);
  });

  return parsed;
}

const FILTER_SYNTAX_OPTIONS = [
  { value: '?', description: 'blank rows' },
  { value: '!?', description: 'non-blank rows' },
  { value: 'VALUE', description: 'contains VALUE' },
  { value: '=VALUE', description: 'exact VALUE only' },
  { value: '!VALUE', description: 'excludes values containing VALUE' },
  { value: '!=VALUE', description: 'excludes exact VALUE only' },
];

function hasParsedFilter(parsed) {
  return parsed.include.length > 0 || parsed.exclude.length > 0 || parsed.includeExact.length > 0 || parsed.excludeExact.length > 0 || parsed.includeBlank || parsed.requireNonBlank;
}

function normalizeFilterCell(value) {
  return safeStr(value).replace(/\u200B/g, '');
}

function tokenMatchesCell(cellLower, token, { allowLeadingHyphenMatch = false } = {}) {
  const t = safeStr(token).toLowerCase();
  if (!t) return false;
  if (cellLower.includes(t)) return true;
  const tNoHyphen = allowLeadingHyphenMatch ? t.replace(/^-+/, '') : '';
  return Boolean(tNoHyphen && cellLower.includes(tNoHyphen));
}

function tokenMatchesCellExactly(cell, token) {
  return safeStr(cell).trim().toLowerCase() === safeStr(token).trim().toLowerCase();
}

function passesParsedFilter(value, parsed, options = {}) {
  if (!hasParsedFilter(parsed)) return true;
  const cell = normalizeFilterCell(value);
  const cellLower = cell.toLowerCase();
  const isBlank = cell.trim() === '';

  if (parsed.includeBlank && parsed.requireNonBlank) return false;
  if (parsed.requireNonBlank && isBlank) return false;

  const needsInclude = parsed.includeBlank || parsed.include.length > 0 || parsed.includeExact.length > 0;
  if (needsInclude) {
    const includeBlankMatch = parsed.includeBlank && isBlank;
    const includeTokenMatch = parsed.include.some(token => tokenMatchesCell(cellLower, token, options));
    const includeExactMatch = parsed.includeExact.some(token => tokenMatchesCellExactly(cell, token));
    if (!includeBlankMatch && !includeTokenMatch && !includeExactMatch) return false;
  }

  return !parsed.exclude.some(token => tokenMatchesCell(cellLower, token, options))
    && !parsed.excludeExact.some(token => tokenMatchesCellExactly(cell, token));
}

function passesTextFilter(value, rawFilter, options = {}) {
  return passesParsedFilter(value, parseFilterExpression(rawFilter), options);
}

function passesSkuFilter(value, rawFilter, skuScopeObj) {
  const parsed = parseFilterExpression(rawFilter);
  if (!skuScopeObj) return passesParsedFilter(value, parsed);

  const scopedParsed = {
    ...parsed,
    include: parsed.include.map(token => (
      skuScopeObj.type === 'prefix' ? safeStr(token).slice(0, skuScopeObj.count) : safeStr(token).slice(-skuScopeObj.count)
    )),
    exclude: parsed.exclude.map(token => (
      skuScopeObj.type === 'prefix' ? safeStr(token).slice(0, skuScopeObj.count) : safeStr(token).slice(-skuScopeObj.count)
    )),
    includeExact: parsed.includeExact.map(token => (
      skuScopeObj.type === 'prefix' ? safeStr(token).slice(0, skuScopeObj.count) : safeStr(token).slice(-skuScopeObj.count)
    )),
    excludeExact: parsed.excludeExact.map(token => (
      skuScopeObj.type === 'prefix' ? safeStr(token).slice(0, skuScopeObj.count) : safeStr(token).slice(-skuScopeObj.count)
    )),
  };
  const sku = safeStr(value);
  const scopedSku = skuScopeObj.type === 'prefix'
    ? sku.substring(0, skuScopeObj.count)
    : sku.slice(-skuScopeObj.count);
  return passesParsedFilter(scopedSku, scopedParsed);
}

function buildClientMSkuText(row) {
  const text = MSKU_COMPONENT_FIELDS
    .map(field => safeStr(row?.[field]).trim())
    .filter(Boolean)
    .join(' | ');
  return text || '';
}


function estimateWidth(header, values) {
  const headerLen = safeStr(header).length;
  const maxCellLen = values.reduce((m, v) => Math.max(m, safeStr(v).length), 0);
  const ch = Math.max(headerLen, maxCellLen);
  const px = 24 + ch * 8.6;
  return Math.max(MIN_AUTOSIZE_WIDTH, Math.min(MAX_AUTOSIZE_WIDTH, Math.round(px)));
}

// "+N"/"-N" scope for SKU
function parseSkuScope(scopeStr) {
  const s = safeStr(scopeStr).trim();
  if (!s) return null;
  const m = s.match(/^([+-])\s*(\d+)$/);
  if (!m) return null;
  const count = parseInt(m[2], 10);
  if (!count || count < 1) return null;
  return { type: m[1] === '+' ? 'prefix' : 'suffix', count };
}

// client post-filter: "?" blanks, "!?" non-blanks, "!TERM" excludes, comma lists mix include/exclude tokens.
function applyClientFilters(rows, filters, skuScopeObj) {
  let res = rows;

  for (const [field, value] of Object.entries(filters)) {
    const v = safeStr(value).trim();
    if (!v) continue;
    if (field === 'sku') {
      res = res.filter(r => passesSkuFilter(r?.sku, v, skuScopeObj));
    } else {
      res = res.filter(r => passesTextFilter(r?.[field], v));
    }
  }

  return res;
}

function applyLocalHeaderFilters(rows, headerFilters) {
  const active = Object.entries(headerFilters)
    .filter(([_, v]) => safeStr(v).trim().length > 0);

  if (!active.length) return rows;

  return rows.filter(row => active.every(([field, qRaw]) =>
    passesTextFilter(row?.[field], qRaw, { allowLeadingHyphenMatch: true })
  ));
}


// ===== Component =====
export default function UploadSKUsAdmin() {
  const navigate = useNavigate();

  // ---------- persisted state (load once) ----------
  const persisted = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; }
  }, []);

  // basic
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [uploadKind, setUploadKind] = useState('');
  const [uploadAccounts, setUploadAccounts] = useState([]);
  const [uploadAccountName, setUploadAccountName] = useState(persisted.uploadAccountName || '');
  const [uploadGroup, setUploadGroup] = useState(persisted.uploadGroup || accountGroupFromName(persisted.uploadAccountName));

  // grid data
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  // inside the component function (before return)
  const CustomGridToolbar = () => {
    return (
      <GridToolbarContainer sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </GridToolbarContainer>
    );
  };



  // grid UI / behavior
  const [density, setDensity] = useState(persisted.density || 'standard');  // 'compact' | 'standard' | 'comfortable'
  const [imageViewSize, setImageViewSize] = useState(clampImageViewSize(persisted.imageViewSize ?? DEFAULT_IMAGE_VIEW_SIZE));
  const [page, setPage] = useState(persisted.page ?? 0);
  const [pageSize, setPageSize] = useState(clampServerPageSize(persisted.pageSize ?? 100)); // keep ≤100 for MIT
  const [sortModel, setSortModel] = useState(persisted.sortModel || [{ field: 'id', sort: 'desc' }]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [selectAllFiltered, setSelectAllFiltered] = useState(!!persisted.selectAllFiltered);

  // filter scope & visibility
  const [filterScope, setFilterScope] = useState(persisted.filterScope || 'global'); // 'global' | 'local'
  const [showGlobalFilters, setShowGlobalFilters] = useState(persisted.showGlobalFilters ?? true);
  const [showAllRows, setShowAllRows] = useState(persisted.showAllRows ?? false);
  const [localDatasetReady, setLocalDatasetReady] = useState(false);

  // per-filter show/hide model + menu (default: core fields ON, split fields OFF)
  const [filterVisibilityModel, setFilterVisibilityModel] = useState(() => {
    const saved = persisted.filterVisibilityModel || {};
    const base = FILTER_KEYS.reduce((acc, k) => {
      acc[k] = Object.prototype.hasOwnProperty.call(saved, k) ? saved[k] : true;
      return acc;
    }, {});
    SPLIT_FIELDS.forEach(k => {
      if (!Object.prototype.hasOwnProperty.call(saved, k)) base[k] = false;
    });
    return base;
  });

  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const openFilterMenu = (e) => setFilterMenuAnchor(e.currentTarget);
  const closeFilterMenu = () => setFilterMenuAnchor(null);

  // Per-column LOCAL header filters (works only in LOCAL scope)
  const [localHeaderFilters, setLocalHeaderFilters] = useState({});
  const [headerMenu, setHeaderMenu] = useState(null);
  const [headerMenuOptions, setHeaderMenuOptions] = useState([]);
  const headerMenuOpenedAtRef = useRef(0);

  const openHeaderMenu = useCallback((event, col) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    headerMenuOpenedAtRef.current = Date.now();
    setHeaderMenu({
      field: col.field,
      title: col.headerName || fieldLabel(col.field),
      anchorPosition: {
        top: Math.round(rect.bottom + 4),
        left: Math.round(rect.left),
      },
    });
  }, []);

  const closeHeaderMenu = useCallback(() => {
    setHeaderMenu(null);
  }, []);

  const localHeaderFilteredCount = useMemo(() => {
    try {
      const base = filterExcludedSkuRows([...(rows || []), ...(preview || [])]);
      const active = localHeaderFilters && Object.keys(localHeaderFilters).some(k => safeStr(localHeaderFilters[k]).trim().length > 0);
      if (!active) return 0;
      const matched = applyLocalHeaderFilters(base, localHeaderFilters) || [];
      return matched.length;
    } catch {
      return 0;
    }
  }, [rows, preview, localHeaderFilters]);


  // filters
  const [globalFilters, setGlobalFilters] = useState(() => {
    const pf = persisted.globalFilters || {};
    const base = GRID_FIELD_KEYS.reduce((acc, field) => {
      acc[field] = pf[field] ?? '';
      return acc;
    }, {});
    return base;
  });

  const [localQuery, setLocalQuery] = useState(persisted.localQuery ?? '');
  const [skuScope, setSkuScope] = useState(persisted.skuScope ?? '');

  // columns
  const [columnWidths, setColumnWidths] = useState(persisted.columnWidths || {});
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    // default hide split fields
    const defaults = {};
    SPLIT_FIELDS.forEach(f => { defaults[f] = false; });
    GRID_FIELD_KEYS.forEach(f => {
      if (!Object.prototype.hasOwnProperty.call(defaults, f)) defaults[f] = true;
    });
    return { ...defaults, ...(persisted.columnVisibilityModel || {}) };
  });
  const setFieldsVisibility = useCallback((fields, visible) => {
    setFilterVisibilityModel(prev => {
      const next = { ...(prev || {}) };
      fields.forEach(field => { next[field] = visible; });
      return next;
    });
    setColumnVisibilityModel(prev => {
      const next = { ...(prev || {}) };
      fields.forEach(field => { next[field] = visible; });
      return next;
    });
  }, []);
  const toggleFieldVisibility = useCallback((field) => {
    const currentlyVisible = filterVisibilityModel?.[field] !== false && columnVisibilityModel?.[field] !== false;
    setFieldsVisibility([field], !currentlyVisible);
  }, [filterVisibilityModel, columnVisibilityModel, setFieldsVisibility]);
  const selectAllFields = useCallback(() => setFieldsVisibility(FILTER_KEYS, true), [setFieldsVisibility]);
  const deselectAllFields = useCallback(() => setFieldsVisibility(FILTER_KEYS, false), [setFieldsVisibility]);
  const applyImageViewSize = useCallback((value) => {
    const next = clampImageViewSize(value);
    setImageViewSize(next);
    setColumnWidths(prev => ({ ...(prev || {}), image_url: imageColumnWidth(next) }));
  }, []);


  // timers & fetch control
  const renderStartRef = useRef(0);
  const inFlightAborters = useRef([]);   // AbortControllers for parallel fetch
  const fetchSeq = useRef(0);            // increment to ignore stale results
  const localFullLoadInFlight = useRef(false);
  const localFullLoadToken = useRef(0);
  const localTopScrollerRef = useRef(null);
  const localGridScrollerRef = useRef(null);
  const [localGridScrollTop, setLocalGridScrollTop] = useState(0);
  const [localGridViewportHeight, setLocalGridViewportHeight] = useState(520);

  // derived
  const isGlobalFiltered = useMemo(
    () => hasActiveFilters(globalFilters),
    [globalFilters]
  );
  const isUnlimitedMode = filterScope === 'global' && isGlobalFiltered;
  const isFullGridMode = filterScope === 'local' || isUnlimitedMode || showAllRows;

  const isClientLocal = filterScope === 'local';

  // persist changes
  useEffect(() => {
    const payload = {
      density, page, pageSize, sortModel,
      imageViewSize,
      filterScope, showGlobalFilters, showAllRows,
      globalFilters, localQuery, skuScope,
      columnWidths, columnVisibilityModel,
      selectAllFiltered,
      filterVisibilityModel,
      uploadAccountName,
      uploadGroup
    };
    try { localStorage.setItem(LS_KEY, JSON.stringify(payload)); } catch {}
  }, [
    density, page, pageSize, sortModel,
    imageViewSize,
    filterScope, showGlobalFilters, showAllRows,
    globalFilters, localQuery, skuScope,
    columnWidths, columnVisibilityModel,
    selectAllFiltered,
    filterVisibilityModel,
    uploadAccountName,
    uploadGroup
  ]);

  useEffect(() => {
    let cancelled = false;
    api.get('/api/accounts?active_only=true')
      .then(({ data }) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setUploadAccounts(list);
        if (list.length === 1) {
          setUploadAccountName(prev => prev || list[0]?.name || '');
          setUploadGroup(prev => prev || accountGroupFromName(list[0]?.name || ''));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const uploadAccountGroups = useMemo(() => {
    const groups = Array.from(new Set(
      (uploadAccounts || [])
        .map(account => accountGroupFromName(account?.name))
        .filter(Boolean)
    ));
    return groups.sort((a, b) => {
      const order = { FK: 1, MY: 2 };
      return (order[a] || 99) - (order[b] || 99) || a.localeCompare(b);
    });
  }, [uploadAccounts]);

  const uploadAccountOptions = useMemo(() => (
    (uploadAccounts || [])
      .filter(account => !uploadGroup || accountGroupFromName(account?.name) === uploadGroup)
      .slice()
      .sort((a, b) => accountSubgroupFromName(a?.name).localeCompare(accountSubgroupFromName(b?.name)))
  ), [uploadAccounts, uploadGroup]);

  useEffect(() => {
    if (uploadGroup || !uploadAccountGroups.length) return;
    setUploadGroup(uploadAccountGroups[0]);
  }, [uploadGroup, uploadAccountGroups]);

  useEffect(() => {
    if (!uploadGroup) return;
    if (uploadAccountName && accountGroupFromName(uploadAccountName) === uploadGroup) return;
    setUploadAccountName('');
  }, [uploadGroup, uploadAccountName]);

  useEffect(() => {
    if (uploadKind !== 'flipkart_inventory' || !preview.length) return;
    setPreview(prev => prev.map(row => ({ ...row, account_name: uploadAccountName })));
  }, [uploadAccountName, uploadKind, preview.length]);

  const FETCH_PROGRESS_THROTTLE = 250; // ms

  const activeFilterLabel = (f = {}) => {
    const actives = Object.entries(f)
      .filter(([k,v]) => k !== 'id' && (String(v ?? '').trim().length > 0))
      .map(([k,v]) => `${k}=${String(v).trim()}`);
    const head = actives.slice(0,3).join(', ');
    return actives.length > 3 ? `${head}, …` : head;
  };

  // ---------- Resizable header component (edge-aligned blue separator + handle) ----------
  function ResizableHeader({
    col,
    columnWidths,
    setColumnWidths,
    minWidth = MIN_AUTOSIZE_WIDTH,
    maxWidth = MAX_AUTOSIZE_WIDTH,
    children
  }) {
    const startXRef = useRef(0);
    const startWRef = useRef(0);
    const draggingRef = useRef(false);

    // HOISTED handlers so cleanup can reference them
    function onMouseMove(e) {
      if (!draggingRef.current) return;
      const dx = e.clientX - startXRef.current;
      const newW = Math.max(minWidth, Math.min(maxWidth, Math.round(startWRef.current + dx)));
      setColumnWidths(prev => ({ ...prev, [col.field]: newW }));
    }

    function onMouseUp() {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mouseleave', onMouseUp);
    }

    function onMouseDown(e) {
      // stop header clicks (sorting / menu) when starting a drag
      e.stopPropagation();
      e.preventDefault();
      draggingRef.current = true;
      startXRef.current = e.clientX;
      startWRef.current = Number(columnWidths?.[col.field] ?? col.width ?? 120);
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
      window.addEventListener('mouseleave', onMouseUp);
    }

    useEffect(() => {
      return () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
        window.removeEventListener('mouseleave', onMouseUp);
        draggingRef.current = false;
      };
    }, []);

    return (
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          paddingRight: 8,
          height: '100%',
          // ensure the header content doesn't sit under the visible blue bar
          paddingLeft: 6
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', paddingRight: 6 }}>
          {children}
        </div>

        {/* Edge aligned handle: invisible wide hit area, blue visible 2px line at edge */}
        <div
          onMouseDown={onMouseDown}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 12,               // wide hit area for easier grabbing
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end', // keep blue bar at the extreme right
            cursor: 'col-resize',
            zIndex: 40,
            userSelect: 'none',
            // allow pointer events so it catches mouse events
          }}
          title="Drag to resize column"
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.02)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          {/* visible blue thin bar exactly at right edge */}
          <div style={{
            width: 2,
            height: '72%',                     // slightly inset visually
            background: '#1976d2',             // MUI primary blue - change if you want different blue
            borderRadius: 1,
            boxShadow: '0 0 0 1px rgba(25,118,210,0.07) inset'
          }} />
        </div>
      </div>
    );
  }


  // ----- columns (with highlight + resizable + visibility) -----
  const baseColumns = useMemo(() => {
  const core = [
    {
      field: '__row_number',
      headerName: 'ROW',
      width: 76,
      minWidth: 64,
      maxWidth: 92,
      sortable: false,
      filterable: false,
      resizable: false,
      disableColumnMenu: true,
      hideable: false,
      align: 'center',
      headerAlign: 'center',
    },
    { field: 'sku', headerName: 'SKU', width: 300, minWidth: MIN_AUTOSIZE_WIDTH, resizable: true },
    { field: 'fsn', headerName: 'FSN', width: 170, minWidth: MIN_AUTOSIZE_WIDTH, resizable: true },
    { field: 'image_url', headerName: 'IMAGE', width: imageColumnWidth(imageViewSize), minWidth: 96, resizable: true, sortable: false },
    { field: 'variant_count', headerName: 'VAR', width: 90, minWidth: 72, resizable: true },
    { field: 'stock_quantity', headerName: 'STOCK', width: 110, minWidth: 90, resizable: true },
    { field: 'real_skus', headerName: 'REAL SKU', width: 360, minWidth: MIN_AUTOSIZE_WIDTH, resizable: true, sortable: false },
    { field: 'account_name', headerName: 'A/C', width: 200, minWidth: MIN_AUTOSIZE_WIDTH, resizable: true },
    { field: 'brand', headerName: 'BRAND', width: 140, minWidth: MIN_AUTOSIZE_WIDTH, resizable: true },
    { field: 'style', headerName: 'STYLE', width: 140, minWidth: MIN_AUTOSIZE_WIDTH, resizable: true },
    { field: 'cmb', headerName: 'CMB', width: 120, minWidth: MIN_AUTOSIZE_WIDTH, resizable: true },
    { field: 'frt_bck', headerName: 'FRT/BCK', width: 130, minWidth: MIN_AUTOSIZE_WIDTH, resizable: true },
    { field: 'print1', headerName: 'PRINT', width: 140, minWidth: MIN_AUTOSIZE_WIDTH, resizable: true },
    { field: 'color1', headerName: 'COLOR', width: 140, minWidth: MIN_AUTOSIZE_WIDTH, resizable: true },
    { field: 'style2', headerName: 'STYLE2', width: 140, minWidth: MIN_AUTOSIZE_WIDTH, resizable: true },
    { field: 'frt_bck1', headerName: 'FRT/BCK1', width: 130, minWidth: MIN_AUTOSIZE_WIDTH, resizable: true },
    { field: 'print2', headerName: 'PRINT2', width: 140, minWidth: MIN_AUTOSIZE_WIDTH, resizable: true },
    { field: 'color2', headerName: 'COLOR2', width: 140, minWidth: MIN_AUTOSIZE_WIDTH, resizable: true },
    { field: 'size', headerName: 'SIZE', width: 120, minWidth: MIN_AUTOSIZE_WIDTH, resizable: true },
    { field: 'm_sku_text', headerName: 'M.SKU', width: 220, flex: 1, minWidth: MIN_AUTOSIZE_WIDTH, resizable: true },
    { field: 'remarks', headerName: 'REMARKS', width: 220, minWidth: MIN_AUTOSIZE_WIDTH, resizable: true },
  ];
  const splitCols = SPLIT_FIELDS.map((f) => ({
    field: f,
    headerName: fieldLabel(f),
    width: 120,
    minWidth: MIN_AUTOSIZE_WIDTH,
    resizable: true,
    sortable: true,
  }));
  return [...core, ...splitCols];
}, [imageViewSize]);


  // ------------------ uniqueValues (respecting other column filters) ------------------
  // Computes unique options per-field using the rows that remain AFTER other filters.
  // Works in LOCAL mode (full client filtering), in UNLIMITED GLOBAL (we loaded all rows),
  // and best-effort in PAGINATED GLOBAL (uses currently loaded rows/page).
  const rowsForUnique = useCallback((field) => {
    // helper to clone filters but exclude the current field
    const withoutField = (obj, f) => {
      const copy = { ...(obj || {}) };
      delete copy[f];
      return copy;
    };

    // base candidate rows:
    // - include preview rows (so after CSV import you get options immediately)
    const baseRows = [...(rows || []), ...(preview || [])];

    // SKU scope object for applyClientFilters
    const skuScopeObj = parseSkuScope(skuScope);

    if (filterScope === 'local') {
      // LOCAL: we want values from rows after applying ALL OTHER filters (global + other local header filters)
      const otherGlobal = withoutField(globalFilters, field);
      const otherLocal = (() => {
        const copy = { ...(localHeaderFilters || {}) };
        delete copy[field];
        return copy;
      })();

      // apply client-side multi/scalar/blank filters for otherGlobal
      const afterGlobal = applyClientFilters(baseRows, otherGlobal, skuScopeObj);

      // apply local header filters (AND across columns) excluding current field
      const afterLocalHeaders = applyLocalHeaderFilters(afterGlobal, otherLocal);

      return afterLocalHeaders;
    }

    // GLOBAL scope:
    // - if unlimited (we fetched all matching rows), rows already contains allRows (server-side filter applied),
    //   but we still need to exclude the current field's filter when computing the unique set.
    // - if paginated, we only have a page — best-effort: compute from current page but exclude current field filter.
    const otherGlobal = withoutField(globalFilters, field);

    // For unlimited mode we expect `rows` to be the full dataset for current globalFilters.
    // For paginated mode `rows` is only current page (so user sees limited options).
    const candidate = rows || [];

    // applyClientFilters will handle '?', multi-values, and SKU scope (we exclude this field's filter so its options show
    // values that remain after all other filters are applied).
    const filtered = applyClientFilters(candidate, otherGlobal, skuScopeObj);

    // Note: we DO NOT apply localHeaderFilters here because in GLOBAL scope local header filters are not used;
    // if you want localHeaderFilters to influence GLOBAL dropdowns too, add similar exclusion logic.
    return filtered;
  }, [rows, preview, skuScope, filterScope, globalFilters, localHeaderFilters]);

  
  

  // ----- cancellable chunk fetch (no per-chunk toast) -----
  const fetchGlobalChunk = async ({ page, sortModel, filters, signal, lite = false, noCount = false }) => {
    const sort = sortModel?.[0] || { field: 'id', sort: 'desc' };
    const { scalar, multi } = normalizeFilters(filters);

    const params = {
      page,
      pageSize: lite ? LITE_CHUNK_SIZE : GLOBAL_CHUNK_SIZE,
      sortBy: sort.field,
      sortDir: sort.sort === 'asc' ? 'asc' : 'desc',
      ...scalar,                       // normal single-value fields
      sku_scope: skuScope || undefined,
      unlimited: 1,
    };
    if (lite) params.lite = 1;
    if (noCount) params.no_count = 1;

    // Attach multi-value lists as a single JSON param
    const hasMulti = Object.keys(multi).length > 0;
    if (hasMulti) {
      params.filters_any = JSON.stringify(multi); // e.g., {"brand":["NIKE","ADIDAS"],"size":["M","L"]}
    }

    const { data } = await api.get('/api/skus', { params, signal });
    return data; // { data: [], total }
  };

  // ----- fast parallel fetch-all with progress & cancellation -----
  const fetchAllGlobalFast = async ({ sortModel, filters, knownTotal, toastId, label, lite = true }) => {
  // cancel any previous run
  inFlightAborters.current.forEach(a => a.abort());
  inFlightAborters.current = [];

  const myToastId = toastId ?? toast.loading('Fetching… 0/0');

  // define these BEFORE any calls
  let lastUpdateTs = 0;
  const updateProgress = (opts = {}, force = false) => {
    const now = performance.now();
    if (!force && now - lastUpdateTs < FETCH_PROGRESS_THROTTLE) return;
    lastUpdateTs = now;
    const pagesDone = pageMap.size; // pageMap is defined below but exists by first call
    const { lastCount = 0, lastMs = 0, whichPage = '?' } = opts;
    const prefix = label ? `Filtering (${label})` : 'Filtering';
    toast.update(myToastId, {
      render: `${prefix}… ${Math.min(fetched, total)}/${total} (${pagesDone}/${totalPages}) — page ${whichPage} last ${lastCount} in ${lastMs} ms`,
      isLoading: true
    });
  };

  // first page -> total
  let total = Number(knownTotal || 0);
  const tFirst = performance.now();
  const acFirst = new AbortController();
  inFlightAborters.current.push(acFirst);
  const first = await fetchGlobalChunk({ page: 1, sortModel, filters, signal: acFirst.signal, lite });

  const firstRows = first.data || [];
  if (!total) total = Number(first.total ?? firstRows.length);
  let fetched = firstRows.length;

  const chunkSize = lite ? LITE_CHUNK_SIZE : GLOBAL_CHUNK_SIZE;
  const totalPages = Math.max(1, Math.ceil(total / chunkSize));
  const pendingPages = [];
  for (let p = 2; p <= totalPages; p++) pendingPages.push(p);

  const pageMap = new Map();
  pageMap.set(1, firstRows);
  console.log('firstRows', firstRows.length, 'total', total);
  // progress for page 1 immediately (force = true)
  updateProgress({
    whichPage: 1,
    lastCount: firstRows.length,
    lastMs: (performance.now() - tFirst).toFixed(0)
  }, true);
  

  if (totalPages === 1) {
    const all = firstRows;
    toast.update(myToastId, { render: `Loaded ${all.length} row(s)`, type: 'success', isLoading: false, autoClose: 4000 });
    return { rows: all, total };
  }

  let idx = 0;
  let failedPages = 0;

  const runWorker = async () => {
    while (idx < pendingPages.length) {
      const p = pendingPages[idx++];
      const ac = new AbortController();
      inFlightAborters.current.push(ac);
      const tChunk = performance.now();
      try {
        const res = await fetchGlobalChunk({ page: p, sortModel, filters, signal: ac.signal, lite, noCount: true });
        const chunk = res.data || [];
        fetched += chunk.length;
        pageMap.set(p, chunk);
        updateProgress({ whichPage: p, lastCount: chunk.length, lastMs: (performance.now() - tChunk).toFixed(0) });
      } catch (err) {
        failedPages += 1;
        updateProgress({ whichPage: p, lastCount: 0, lastMs: (performance.now() - tChunk).toFixed(0) });
        console.error('[fetch page failed]', p, err?.message || err);
      }
    }
  };

  const workers = Math.min(UNLIMITED_CONCURRENCY, pendingPages.length);
  if (workers > 0) await Promise.all(new Array(workers).fill(0).map(runWorker));

  const all = [];
  for (let p = 1; p <= totalPages; p++) all.push(...(pageMap.get(p) || []));

  const finalMsg = failedPages
    ? `Loaded ${all.length}/${total} row(s) — ${failedPages} page(s) failed`
    : `Loaded ${all.length} row(s)`;
  toast.update(myToastId, { render: finalMsg, type: failedPages ? 'warning' : 'success', isLoading: false, autoClose: 4000 });

  return { rows: all, total };
};


  // helper: count rows matching current global filters (for confirmation)
  const countMatchingRows = async (filters) => {
    const sort = sortModel?.[0] || { field: 'id', sort: 'desc' };
    const { scalar, multi } = normalizeFilters(filters);

    const params = {
      page: 1,
      pageSize: 1,
      sortBy: sort.field,
      sortDir: sort.sort === 'asc' ? 'asc' : 'desc',
      ...scalar,
      sku_scope: skuScope || undefined,
      unlimited: 1,
    };

    if (Object.keys(multi).length > 0) {
      params.filters_any = JSON.stringify(multi);
    }

    const { data } = await api.get('/api/skus', { params });
    return Number(data?.total ?? 0);
  };


  // ---- helpers for chunked selected-ID updates ----
    const CHUNK_IDS = 5000;       // how many IDs per PATCH
    const PATCH_CONCURRENCY = 2;  // run 2 chunks in parallel (tune if needed)

    function chunkArray(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
    }

    async function runWithConcurrency(items, limit, worker) {
    let i = 0;
    const running = new Set();
    const next = async () => {
        if (i >= items.length) return;
        const index = i++;
        const p = worker(items[index], index).finally(() => running.delete(p));
        running.add(p);
        if (running.size >= limit) await Promise.race(running);
        return next();
    };
    await next();
    await Promise.all(running);
    }


  // ----- main fetch -----
  // ----- main fetch (updated: global => fetch all via fetchAllGlobalFast) -----
const fetchRows = async (options = {}) => {
  const mySeq = ++fetchSeq.current;
  let localLoadToken = 0;
  // cancel any in-flight chunk requests from prior runs
  inFlightAborters.current.forEach(a => a.abort());
  inFlightAborters.current = [];

  // use provided overrides or current state
  const _scope = options.scope ?? filterScope;
  const _page = Number.isFinite(Number(options.page)) ? Number(options.page) : page;
  const _pageSize = clampServerPageSize(options.pageSize ?? pageSize);
  const _sortModel = options.sortModel ?? sortModel;
  const _filters = options.filters ?? globalFilters;
  const forceAll = Boolean(options.forceAll);

  const skuScopeObj = parseSkuScope(skuScope);
  const hasBlankFilters = Object.values(_filters).some(v => safeStr(v).trim() === '?');
  const skuTerm = safeStr(_filters.sku).trim();
  const hasClientOnlyFilters = Object.entries(_filters).some(([field, value]) => CLIENT_ONLY_FILTER_FIELDS.has(field) && safeStr(value).trim());
  const needClientFilter = hasBlankFilters || hasClientOnlyFilters || (skuTerm && skuScopeObj);
  const activeFilters = hasActiveFilters(_filters);

  setLoading(true);
  try {
    if (_scope === 'global') {
      const sort = _sortModel?.[0] || { field: 'id', sort: 'desc' };

      // No global filters: stay in normal server-paginated mode.
      if (!forceAll && !activeFilters) {
        const { data } = await api.get('/api/skus', {
          params: {
            page: _page + 1,
            pageSize: _pageSize,
            sortBy: sort.field,
            sortDir: sort.sort === 'asc' ? 'asc' : 'desc',
          }
        });

        if (mySeq !== fetchSeq.current) return;
        renderStartRef.current = performance.now();
        setRows(data?.data || []);
        setRowCount(Number(data?.total || 0));
        setLocalDatasetReady(false);
        return;
      }

      // Global filters: fetch all matching rows so toolbar/header filters operate on the complete result set.
      const label = activeFilterLabel(_filters);
      const toastId = toast.loading(label ? `Counting (${label})…` : 'Counting…');

      let totalToFetch = 0;
      try { totalToFetch = await countMatchingRows(_filters); } catch {}

      toast.update(toastId, {
        render: label ? `Filtering (${label})… 0/${totalToFetch || '?'}` : `Filtering… 0/${totalToFetch || '?'}`,
        isLoading: true
      });

      const t0 = performance.now();
      const { rows: allRows } = await fetchAllGlobalFast({
        sortModel: _sortModel,
        filters: _filters,
        knownTotal: totalToFetch || undefined,
        toastId,
        label,
        lite: !hasClientOnlyFilters
      });

      if (mySeq !== fetchSeq.current) return; // stale run

      // apply client-only post filters (blanks / sku-scope) if needed
      const filtered = needClientFilter ? applyClientFilters(allRows, _filters, skuScopeObj) : allRows;

      renderStartRef.current = performance.now();
      setRows(filtered);
      setRowCount(filtered.length);
      setLocalDatasetReady(false);
      const t1 = performance.now();
      toast.update(toastId, {
        render: `Loaded ${filtered.length} row(s) in ${(t1 - t0).toFixed(0)} ms`,
        type: 'success',
        isLoading: false,
        autoClose: 4000
      });
      return;
    }

    // LOCAL: ensure rows contain the complete unfiltered SKU master, then filter in memory.
    if (_scope === 'local') {
      const looksLikePaginatedCache = rowCount > 0 && (rows || []).length > 0 && (rows || []).length < rowCount;
      const tooSmallForLocalCache = (rows || []).length > 0 && (rows || []).length <= pageSize;
      const mustLoadLocalAll = forceAll || !localDatasetReady || !rows || rows.length === 0 || looksLikePaginatedCache || tooSmallForLocalCache;

      if (mustLoadLocalAll) {
        localLoadToken = ++localFullLoadToken.current;
        localFullLoadInFlight.current = true;
        const { rows: all } = await fetchAllGlobalFast({
          sortModel: _sortModel,
          filters: {}, // no filters to populate everything
          knownTotal: 0,
          label: options.label || 'Local load all',
          lite: !hasClientOnlyFilters
        });
        if (mySeq !== fetchSeq.current) return;
        renderStartRef.current = performance.now();
        setRows(all);
        setRowCount(all.length);
        setLocalDatasetReady(true);
      }
      return;
    }
  } catch (e) {
    if (e?.name === 'CanceledError' || e?.message === 'canceled') {
      // ignore canceled requests
    } else {
      toast.error('Failed to load: ' + (e?.response?.data?.error || e.message));
    }
  } finally {
    if (localLoadToken && localFullLoadToken.current === localLoadToken) {
      localFullLoadInFlight.current = false;
    }
    setLoading(false);
  }
};


  // initial + effects
  useEffect(() => {
    fetchRows({
      scope: filterScope,
      page: 0,
      pageSize,
      sortModel,
      filters: globalFilters,
      forceAll: filterScope === 'local' || showAllRows || hasActiveFilters(globalFilters),
      label: showAllRows ? 'Load all rows' : undefined,
    });
    /* eslint-disable-next-line */
  }, []);
  useEffect(() => {
    if (filterScope === 'global' && !isFullGridMode) {
      fetchRows({ page, pageSize, sortModel });
    }
    /* eslint-disable-next-line */
  }, [page, pageSize, sortModel, filterScope, isFullGridMode]);
  useEffect(() => {
    if (filterScope !== 'local' || loading || localFullLoadInFlight.current) return;
    const hasRows = rows.length > 0;
    const hasOnlyPageCache = hasRows && (rows.length <= pageSize || (rowCount > 0 && rows.length < rowCount));
    if (!localDatasetReady || !hasRows || hasOnlyPageCache) {
      fetchRows({ scope: 'local', forceAll: true, sortModel, filters: {}, label: 'Local load all' });
    }
    /* eslint-disable-next-line */
  }, [filterScope, loading, rows.length, rowCount, pageSize, localDatasetReady, sortModel]);
  useEffect(() => {
    if (!renderStartRef.current) return;
    requestAnimationFrame(() => {
      const ms = performance.now() - renderStartRef.current;
      toast.info(`Rendered ${rows.length} row(s) in ${ms.toFixed(0)} ms`);
      renderStartRef.current = 0;
    });
  }, [rows]);

  // ----- Actions -----
  const applyGlobalFilters = async () => {
  setPage(0);
  setLocalQuery('');
  setSelectionModel([]);

  const scopeObj = parseSkuScope(skuScope);
  if (scopeObj && !safeStr(globalFilters.sku).trim()) {
    toast.info('SKU scope given but SKU term is empty — searching whole SKU.');
  } else if (scopeObj) {
    toast.info(`SKU scope: ${scopeObj.type === 'prefix' ? 'first' : 'last'} ${scopeObj.count} char(s)`);
  }

  if (filterScope === 'local') {
    try {
      if (!localDatasetReady || !rows.length || rows.length <= pageSize || (rowCount > 0 && rows.length < rowCount)) {
        toast.info('Loading all rows for LOCAL search…');
        await fetchRows({ scope: 'local', forceAll: true, sortModel, label: 'Local search' });
      } else {
        toast.info('Applying LOCAL filters…');
      }
    } catch (err) {
      toast.error('LOCAL search failed: ' + (err?.response?.data?.error || err.message || 'unknown'));
    }
    return;
  }

  // GLOBAL: fetch all matching rows when filters are active or All Rows is enabled.
  try {
    toast.info(
      hasActiveFilters(globalFilters) || showAllRows
        ? 'GLOBAL search… counting & fetching matching rows'
        : 'GLOBAL search… loading first page'
    );
    await fetchRows({
      scope: 'global',
      page: 0,
      pageSize,
      sortModel,
      filters: globalFilters,
      forceAll: showAllRows || hasActiveFilters(globalFilters),
    });
  } catch (err) {
    toast.error('GLOBAL fetch failed: ' + (err?.response?.data?.error || err.message || 'unknown'));
  }
};



  const clearGlobalFilters = () => {
    const cleared = GRID_FIELD_KEYS.reduce((acc, field) => {
      acc[field] = '';
      return acc;
    }, {});
    setGlobalFilters(cleared);
    setSkuScope('');
    setPage(0);
    setSelectionModel([]);
    if (filterScope === 'local') {
      setLocalDatasetReady(false);
      if (localGridScrollerRef.current) {
        localGridScrollerRef.current.scrollTop = 0;
        localGridScrollerRef.current.scrollLeft = 0;
      }
      if (localTopScrollerRef.current) localTopScrollerRef.current.scrollLeft = 0;
      setLocalGridScrollTop(0);
    }
    toast.info('Global filters cleared');
    fetchRows({
      scope: filterScope,
      page: 0,
      pageSize,
      sortModel,
      filters: cleared,
      forceAll: filterScope === 'local' || showAllRows,
      label: showAllRows ? 'Load all rows' : undefined,
    });
  };

  const localDisplayRows = useMemo(() => {
    // Only apply client-side logic in LOCAL mode; otherwise return rows directly.
    if (filterScope !== 'local') return rows;

    const skuScopeObj = parseSkuScope(skuScope);
    let res = applyClientFilters(rows || [], globalFilters, skuScopeObj);

    // Apply local quick-search (single-box search across all columns) if present
    if (localQuery && String(localQuery).trim()) {
      const q = String(localQuery).trim().toLowerCase();
      res = res.filter(r => Object.values(r || {}).some(val => safeStr(val).toLowerCase().includes(q)));
    }

    // Apply per-column LOCAL header filters (AND across fields)
    if (localHeaderFilters && Object.keys(localHeaderFilters).some(k => safeStr(localHeaderFilters[k]).trim().length > 0)) {
      res = applyLocalHeaderFilters(res, localHeaderFilters);
    }

    return res;
  }, [rows, filterScope, globalFilters, skuScope, localQuery, localHeaderFilters]);

  // Compute rows to render in the grid: always apply localHeaderFilters to the currently loaded rows.
  // When filterScope === 'local' we also apply local quick-search and header filters (existing localDisplayRows does that).
  // When filterScope === 'global' we still want header inputs to filter the *current in-memory rows* (no server calls).
  const visibleRows = useMemo(() => {
    // base: rows currently loaded from server + preview so filters apply immediately when user uploads preview
    const base = filterExcludedSkuRows([...(rows || []), ...(preview || [])]);

    // Apply local quick-search only when user is explicitly in LOCAL scope (keeps current behavior).
    let r = (filterScope === 'local') ? localDisplayRows : base;

    // In GLOBAL scope, header inputs filter the currently loaded server result in memory.
    // LOCAL already applies header filters in localDisplayRows.
    if (filterScope !== 'local' && localHeaderFilters && Object.keys(localHeaderFilters).some(k => safeStr(localHeaderFilters[k]).trim().length > 0)) {
      r = applyLocalHeaderFilters(r, localHeaderFilters);
    }

    return r;
  }, [rows, preview, localDisplayRows, localHeaderFilters, filterScope]);

  useEffect(() => {
    const field = headerMenu?.field;
    if (!field) {
      setHeaderMenuOptions([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      const candidates = rowsForUnique(field) || [];
      const seen = new Set();

      for (let i = 0; i < candidates.length; i += 1) {
        const val = safeStr(candidates[i]?.[field]);
        if (!val) continue;

        if (val.includes(',')) {
          const parts = splitCSV(val);
          for (let j = 0; j < parts.length; j += 1) {
            seen.add(parts[j]);
            if (seen.size >= UNIQUE_VALUE_LIMIT) break;
          }
        } else {
          seen.add(val);
        }

        if (seen.size >= UNIQUE_VALUE_LIMIT) break;
      }

      const nextOptions = Array.from(seen)
        .sort((a, b) => a.localeCompare(b))
        .slice(0, UNIQUE_VALUE_LIMIT);

      if (!cancelled) setHeaderMenuOptions(nextOptions);
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [headerMenu?.field, rowsForUnique]);


  // Drop-in replacement for your ColumnHeaderWithMenuFactory
  function ColumnHeaderWithMenuFactory({
    localHeaderFilters,
    setLocalHeaderFilters,
    openHeaderMenu
  }) {
    return function ColumnHeaderWithMenu({ col }) {
      const field = col.field;
      const title = col.headerName || fieldLabel(field);
      const currentHeaderFilter = localHeaderFilters[field];
      const [headerInput, setHeaderInput] = useState(safeStr(currentHeaderFilter || ''));

      useEffect(() => {
        setHeaderInput(safeStr(currentHeaderFilter || ''));
      }, [field, currentHeaderFilter]);

      useEffect(() => {
        const id = setTimeout(() => {
          setLocalHeaderFilters(prev => ({ ...prev, [field]: headerInput }));
        }, 300);
        return () => clearTimeout(id);
      }, [headerInput, field]);

      return (
        <Stack spacing={0.5} sx={{ py: 0.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1 }}>{title}</Typography>

            <Button
              size="small"
              onClick={(e) => openHeaderMenu(e, col)}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{ minWidth: 10, height: 22, p: '4px' }}
              aria-label={`Open column menu for ${title}`}
              className="custom-col-menu-btn"
            >
              ⋮
            </Button>
          </Stack>

          <TextField
            size="small"
            placeholder="filter (client)"
            value={headerInput}
            onChange={(e) => { e.stopPropagation(); setHeaderInput(e.target.value); }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 }, minWidth: 80 }}
          />
        </Stack>
      );
    };
  }


  const columns = useMemo(() => {
    const renderCell = makeRenderCell({ filterScope, localQuery, globalFilters, localHeaderFilters });

    // instantiate the header component factory with current refs/state
  const ColumnHeaderWithMenu = ColumnHeaderWithMenuFactory({
    localHeaderFilters,
    setLocalHeaderFilters,
    openHeaderMenu
  });


    return baseColumns.map(col => ({
      ...col,
      width: columnWidths[col.field] ?? col.width,
      renderCell: col.field === '__row_number'
        ? (params) => {
            const index = params.api.getRowIndexRelativeToVisibleRows(params.id);
            if (!Number.isFinite(index) || index < 0) return '';
            const pageOffset = filterScope === 'global' && !isFullGridMode ? page * pageSize : 0;
            return pageOffset + index + 1;
          }
        : col.field === 'image_url'
          ? (params) => {
              const url = safeStr(params.value);
              const error = safeStr(params.row?.image_error);
              if (!url) {
                return (
                  <Tooltip title={error || 'No image fetched'}>
                    <Typography variant="caption" color={error ? 'error' : 'text.secondary'}>
                      {error ? 'ERR' : '-'}
                    </Typography>
                  </Tooltip>
                );
              }
              return (
                <Tooltip title="Open image">
                  <Box
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                      width: imageViewSize,
                      height: imageViewSize,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #d1d5db',
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: '#fff',
                    }}
                  >
                    <Box
                      component="img"
                      src={url}
                      alt={params.row?.fsn || params.row?.sku || 'SKU image'}
                      loading="lazy"
                      sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                </Tooltip>
              );
            }
        : renderCell,

      // renderHeader composes the menu/header inside the resizable wrapper
      renderHeader: (params) => {
        if (col.field === '__row_number') {
          return (
            <Box sx={{ width: '100%', textAlign: 'center', fontSize: 12, fontWeight: 700 }}>
              ROW
            </Box>
          );
        }

        const headerContent = <ColumnHeaderWithMenu col={col} />;
        return (
          <ResizableHeader
            col={col}
            columnWidths={columnWidths}
            setColumnWidths={setColumnWidths}
          >
            {headerContent}
          </ResizableHeader>
        );
      }
    }));
  }, [baseColumns, columnWidths, filterScope, isFullGridMode, page, pageSize, localQuery, globalFilters, localHeaderFilters, setColumnWidths, openHeaderMenu, imageViewSize]);

  const localGridColumns = useMemo(
    () => columns.filter(col => col.field === '__row_number' || columnVisibilityModel?.[col.field] !== false),
    [columns, columnVisibilityModel]
  );

  const localGridRows = useMemo(() => {
    if (filterScope !== 'local') return visibleRows;
    const activeSort = sortModel?.[0];
    if (!activeSort?.field || activeSort.field === '__row_number') return visibleRows;
    const dir = activeSort.sort === 'asc' ? 1 : -1;
    return [...visibleRows].sort((a, b) => compareGridValues(a?.[activeSort.field], b?.[activeSort.field]) * dir);
  }, [filterScope, visibleRows, sortModel]);

  const gridRowHeight = imageRowHeight(density, imageViewSize);
  const localRowHeight = gridRowHeight;
  const localGridWidth = useMemo(() => (
    LOCAL_SELECTION_COLUMN_WIDTH + localGridColumns.reduce((sum, col) => (
      sum + Number(columnWidths?.[col.field] ?? col.width ?? col.minWidth ?? 120)
    ), 0)
  ), [localGridColumns, columnWidths]);
  const localSelectedIds = useMemo(() => new Set(selectionModel.map(Number)), [selectionModel]);
  const localVisibleIds = useMemo(
    () => localGridRows.map(r => Number(r?.id)).filter(Number.isFinite),
    [localGridRows]
  );
  const allLocalRowsSelected = localVisibleIds.length > 0 && localVisibleIds.every(id => localSelectedIds.has(id));
  const someLocalRowsSelected = localVisibleIds.some(id => localSelectedIds.has(id));
  const localGridBodyOffset = Math.max(0, localGridScrollTop - LOCAL_HEADER_HEIGHT);
  const localStartIndex = Math.max(0, Math.floor(localGridBodyOffset / localRowHeight) - LOCAL_VIRTUAL_OVERSCAN);
  const localEndIndex = Math.min(
    localGridRows.length,
    Math.ceil((localGridBodyOffset + localGridViewportHeight) / localRowHeight) + LOCAL_VIRTUAL_OVERSCAN
  );
  const localVirtualRows = useMemo(
    () => localGridRows.slice(localStartIndex, localEndIndex).map((row, offset) => ({
      row,
      index: localStartIndex + offset,
    })),
    [localGridRows, localStartIndex, localEndIndex]
  );

  const handleLocalGridScroll = useCallback((event) => {
    const scroller = event.currentTarget;
    setLocalGridScrollTop(scroller.scrollTop);
    setLocalGridViewportHeight(scroller.clientHeight || 520);
    const topScroller = localTopScrollerRef.current;
    if (topScroller && Math.abs(topScroller.scrollLeft - scroller.scrollLeft) > 1) {
      topScroller.scrollLeft = scroller.scrollLeft;
    }
  }, []);

  const handleLocalTopScroll = useCallback((event) => {
    const gridScroller = localGridScrollerRef.current;
    if (gridScroller && Math.abs(gridScroller.scrollLeft - event.currentTarget.scrollLeft) > 1) {
      gridScroller.scrollLeft = event.currentTarget.scrollLeft;
    }
  }, []);

  const toggleLocalRowSelection = useCallback((rowId, checked) => {
    const numericId = Number(rowId);
    if (!Number.isFinite(numericId)) return;
    setSelectionModel(prev => {
      const next = new Set(prev.map(Number));
      if (checked) next.add(numericId); else next.delete(numericId);
      return Array.from(next);
    });
  }, []);

  const toggleAllLocalRowsSelection = useCallback((checked) => {
    setSelectionModel(prev => {
      const next = new Set(prev.map(Number));
      localVisibleIds.forEach(id => {
        if (checked) next.add(id); else next.delete(id);
      });
      return Array.from(next);
    });
  }, [localVisibleIds]);

  useEffect(() => {
    if (filterScope !== 'local') return;
    const el = localGridScrollerRef.current;
    if (!el) return;
    setLocalGridViewportHeight(el.clientHeight || 520);
  }, [filterScope]);

  useEffect(() => {
    if (filterScope !== 'local') return;
    const el = localGridScrollerRef.current;
    if (!el) return;
    el.scrollTop = 0;
    el.scrollLeft = 0;
    if (localTopScrollerRef.current) localTopScrollerRef.current.scrollLeft = 0;
    setLocalGridScrollTop(0);
    setLocalGridViewportHeight(el.clientHeight || 520);
  }, [filterScope, visibleRows.length]);



  const onScopeChange = async (_e, val) => {
    if (!val) return;
    setFilterScope(val);
    setLocalQuery('');
    setPage(0);
    setSelectionModel([]);
    setLocalHeaderFilters({});
    toast.info(`Scope: ${val.toUpperCase()}`);

    try {
      if (val === 'local') {
        toast.info('Loading all rows for LOCAL mode…');
        await fetchRows({
          scope: 'local',
          forceAll: true,
          filters: {},
          sortModel,
          label: 'Local load all',
        });
        return;
      }

      setLocalDatasetReady(false);
      await fetchRows({
        scope: 'global',
        page: 0,
        pageSize,
        sortModel,
        filters: globalFilters,
        forceAll: showAllRows || hasActiveFilters(globalFilters),
        label: showAllRows ? 'Load all rows' : undefined,
      });
    } catch (err) {
      toast.error(`Failed to switch to ${val.toUpperCase()}: ${err?.response?.data?.error || err?.message || 'unknown'}`);
    }
  };


  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f); setPreview([]); setUploadKind(''); if (!f) return;
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (['xlsx', 'xls'].includes(ext)) {
      toast.info('Excel file selected. Preview is available for CSV only; upload will be processed on the server. Select account if it is Flipkart inventory.');
      return;
    }
    if (ext !== 'csv') {
      toast.error('Unsupported file type. Choose .csv, .xlsx, or .xls');
      setFile(null);
      return;
    }
    const t0 = performance.now();
    Papa.parse(f, {
      header: true, skipEmptyLines: true, preview: 50,
      complete: (res) => {
        const headers = res.meta.fields || [];
        const kind = detectUploadKind(headers);
        if (!kind) {
          toast.error(`Header mismatch. Required SKU master, Flipkart inventory, or Myntra article file (${MYNTRA_ARTICLE_HEADERS.join(', ')})`);
          setFile(null);
          return;
        }
        setUploadKind(kind);
        if (kind === 'flipkart_inventory' || kind === 'flipkart_listing') {
          setPreview((res.data || [])
            .map(row => kind === 'flipkart_listing'
              ? normalizeFlipkartListingPreviewRow(row, uploadAccountName)
              : normalizeFlipkartPreviewRow(row, uploadAccountName))
            .filter(row => !isSkuExcludedByPrefix(row.sku)));
          toast.info(kind === 'flipkart_listing'
            ? 'Flipkart seller listing export detected. Select the account before upload.'
            : 'Flipkart inventory file detected. Select the account before upload.');
        } else if (kind === 'myntra_article') {
          setPreview((res.data || [])
            .map(row => normalizeMyntraArticlePreviewRow(row, uploadAccountName))
            .filter(row => !isSkuExcludedByPrefix(row.sku)));
          toast.info('Myntra article file detected. Select the SKU Master account before upload.');
        } else {
          setPreview((res.data || []).filter(row => !isSkuExcludedByPrefix(rowValueAny(row, ['SKU', 'sku']))));
        }
        toast.success(`Parsed preview in ${(performance.now()-t0).toFixed(0)} ms`);
      }
    });
  };

  const uploadCsv = async () => {
    if (!file) {
      toast.warn('Choose a CSV or Excel file first');
      return false;
    }
    if ((uploadKind === 'flipkart_inventory' || uploadKind === 'flipkart_listing') && !safeStr(uploadAccountName).trim()) {
      toast.warn('Select account for Flipkart upload');
      return false;
    }
    if (uploadKind === 'myntra_article' && !safeStr(uploadAccountName).trim()) {
      toast.warn('Select account for Myntra article upload');
      return false;
    }
    setLoading(true);
    const t0 = performance.now();
    let toastId = null;
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (uploadAccountName) fd.append('account_name', uploadAccountName);
      const { data } = await api.post('/api/sku/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data?.async && data?.job_id) {
        toastId = toast.loading('Inventory import started: 0 row(s) processed...');
        while (true) {
          await sleep(2500);
          const { data: jobData } = await api.get(`/api/sku/upload-jobs/${data.job_id}`);
          const job = jobData?.job || {};
          const rowsDone = Number(job.rows || 0);
          const variantsDone = Number(job.inventory_variants || 0);
          if (job.status === 'failed') {
            toast.update(toastId, {
              render: `Inventory import failed: ${job.error || 'unknown error'}`,
              type: 'error',
              isLoading: false,
              autoClose: 8000,
            });
            return false;
          }
          if (job.status === 'completed') {
            toast.update(toastId, {
              render: `Inventory import complete: ${rowsDone} row(s), ${variantsDone} real SKU variant(s)`,
              type: 'success',
              isLoading: false,
              autoClose: 7000,
            });
            break;
          }
          toast.update(toastId, {
            render: `Inventory import ${job.status || 'running'}: ${rowsDone} row(s), ${variantsDone} variant(s)...`,
            isLoading: true,
          });
        }
      } else {
      const count = data.rows ?? data.inserted ?? 0;
      const variantText = data.upload_kind === 'flipkart_inventory' || data.upload_kind === 'flipkart_listing'
        ? `, ${data.inventory_variants || 0} real SKU variant row(s)`
        : '';
      toast.success(`Processed ${count} row(s)${variantText} in ${(performance.now()-t0).toFixed(0)} ms`);
      }
      setLocalDatasetReady(false);
      await fetchRows({
        scope: filterScope,
        page: 0,
        pageSize,
        sortModel,
        filters: globalFilters,
        forceAll: filterScope === 'local' || showAllRows || hasActiveFilters(globalFilters),
        label: showAllRows ? 'Load all rows' : undefined,
      });
      return true;
    } catch (e) {
      if (toastId) {
        toast.update(toastId, {
          render: 'Upload failed: ' + (e?.response?.data?.error || e.message),
          type: 'error',
          isLoading: false,
          autoClose: 8000,
        });
      } else {
        toast.error('Upload failed: ' + (e?.response?.data?.error || e.message));
      }
      return false;
    } finally { setLoading(false); }
  };

  const importOrderSkus = async () => {
    setLoading(true);
    const toastId = toast.loading('Importing from orders...');
    const t0 = performance.now();
    try {
      const { data } = await api.post('/api/skus/import-from-orders');
      const ms = (performance.now() - t0).toFixed(0);
      const imported = Number(data.imported || 0);
      const missing = Number(data.missing_bases || 0);
      const variants = Number(data.missing_source_sku_variants || 0);
      toast.update(toastId, {
        render: imported
          ? `Imported ${imported} SKU(s) from ${variants} order SKU variant(s) in ${ms} ms`
          : `No new SKUs found. ${missing} missing base(s), ${data.skipped_existing || 0} already present`,
        type: imported ? 'success' : 'info',
        isLoading: false,
        autoClose: 5000,
      });
      setLocalDatasetReady(false);
      await fetchRows({
        scope: filterScope,
        page: 0,
        pageSize,
        sortModel,
        filters: globalFilters,
        forceAll: filterScope === 'local' || showAllRows || hasActiveFilters(globalFilters),
        label: showAllRows ? 'Load all rows' : undefined,
      });
    } catch (e) {
      toast.update(toastId, {
        render: 'Import failed: ' + (e?.response?.data?.error || e.message || 'unknown error'),
        type: 'error',
        isLoading: false,
        autoClose: 6000,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSelectedImages = async () => {
    const ids = (selectionModel || []).map(Number).filter(Number.isFinite);
    if (!ids.length) return toast.warn('Select rows with FSN first');

    setLoading(true);
    const chunks = [];
    for (let i = 0; i < ids.length; i += IMAGE_FETCH_CHUNK_SIZE) {
      chunks.push(ids.slice(i, i + IMAGE_FETCH_CHUNK_SIZE));
    }
    const totals = {
      fetched: 0,
      skipped: 0,
      failed: 0,
      backfilled: 0,
      checked: 0,
    };
    const failedChunks = [];
    let browserLookupsDone = 0;
    const rowById = new Map((rows || []).map(row => [String(row.id), row]));
    const refreshSavedChunk = async (chunk) => {
      // Re-read only the completed batch. Reloading the entire local dataset here
      // is prohibitively expensive and used to make long fetches look stalled.
      let lastError;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const { data } = await api.get('/api/skus', {
            params: {
              page: 1,
              pageSize: chunk.length,
              lite: '1',
              filters_any: JSON.stringify({ id: chunk }),
              include_disabled: 'all',
            },
          });
          const refreshed = new Map((data?.data || []).map(row => [String(row.id), row]));
          if (refreshed.size) setRows(prev => (prev || []).map(row => refreshed.get(String(row.id)) || row));
          return refreshed;
        } catch (error) {
          lastError = error;
          if (attempt < 2) await sleep((attempt + 1) * 1500);
        }
      }
      throw lastError || new Error('Could not refresh saved image rows');
    };
    const localImageUrl = async (row) => {
      if (!row?.fsn || !/^FK\b/i.test(safeStr(row.account_name))) return null;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 40000);
        try {
          const response = await fetch(`${LOCAL_FLIPKART_BRIDGE}/api/flipkart-console/sku-image`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fsn: row.fsn, sku: row.sku, account_name: row.account_name }),
            signal: controller.signal,
          });
          if (response.ok) {
            const data = await response.json();
            const imageUrl = safeStr(data?.image_url);
            if (imageUrl) return imageUrl;
          }
        } catch {
          // Retry transient bridge timeouts and Flipkart interstitials below.
        } finally {
          clearTimeout(timer);
        }
        if (attempt < 2) await sleep((attempt + 1) * 1500);
      }
      return null;
    };
    const toastId = toast.loading(`Fetching images 0/${ids.length} selected SKU row(s)...`);
    try {
      for (let index = 0; index < chunks.length; index += 1) {
        const chunk = chunks[index];
        toast.update(toastId, {
          render: `Fetching images ${Math.min(index * IMAGE_FETCH_CHUNK_SIZE, ids.length)}/${ids.length} selected SKU row(s)...`,
          isLoading: true,
        });

        try {
          const sourceImages = {};
          const uniqueRows = [];
          const seenImageKeys = new Set();
          chunk.forEach(id => {
            const row = rowById.get(String(id));
            const key = `${safeStr(row?.account_name).toUpperCase()}|${safeStr(row?.fsn).toUpperCase()}`;
            if (row?.fsn && !seenImageKeys.has(key)) {
              seenImageKeys.add(key);
              uniqueRows.push(row);
            }
          });
          const localResults = [];
          let nextLookup = 0;
          const lookupWorker = async () => {
            while (nextLookup < uniqueRows.length) {
              const row = uniqueRows[nextLookup];
              nextLookup += 1;
              const imageUrl = await localImageUrl(row);
              localResults.push({ row, imageUrl });
              browserLookupsDone += 1;
              toast.update(toastId, {
                render: `Browser image lookup ${browserLookupsDone}/${ids.length} selected SKU row(s)...`,
                isLoading: true,
              });
            }
          };
          await Promise.all(Array.from({
            length: Math.min(LOCAL_IMAGE_LOOKUP_CONCURRENCY, uniqueRows.length),
          }, lookupWorker));
          localResults.forEach(({ row, imageUrl }) => {
            if (!imageUrl) return;
            chunk.forEach(id => {
              const candidate = rowById.get(String(id));
              if (candidate && safeStr(candidate.fsn).toUpperCase() === safeStr(row.fsn).toUpperCase()
                && safeStr(candidate.account_name).toUpperCase() === safeStr(row.account_name).toUpperCase()) {
                sourceImages[String(id)] = imageUrl;
              }
            });
          });
          let data;
          let saveError;
          for (let attempt = 0; attempt < 3; attempt += 1) {
            try {
              ({ data } = await api.post('/api/skus/fetch-images', {
                ids: chunk,
                marketplace: 'auto',
                concurrency: IMAGE_FETCH_CONCURRENCY,
                source_images: sourceImages,
              }));
              saveError = null;
              break;
            } catch (error) {
              saveError = error;
              if (attempt < 2) await sleep((attempt + 1) * 2000);
            }
          }
          if (saveError) throw saveError;
          totals.fetched += Number(data?.fetched || 0);
          totals.skipped += Number(data?.skipped || 0);
          totals.failed += Number(data?.failed || 0);
          totals.backfilled += Number(data?.backfilled || 0);
          totals.checked += Number(data?.checked || chunk.length);
          toast.update(toastId, {
            render: `Saved batch ${index + 1}/${chunks.length}; refreshing rows…`,
            isLoading: true,
          });
          let verifiedRows = await refreshSavedChunk(chunk);
          const missingSavedIds = chunk.filter(id => {
            const sourceRow = rowById.get(String(id));
            if (!sourceRow?.fsn || !/^FK\b/i.test(safeStr(sourceRow.account_name))) return false;
            return !safeStr(verifiedRows?.get(String(id))?.image_url);
          });
          if (missingSavedIds.length) {
            toast.update(toastId, {
              render: `Verifying batch ${index + 1}/${chunks.length}; retrying ${missingSavedIds.length} unsaved row(s)…`,
              isLoading: true,
            });
            const retrySourceImages = {};
            missingSavedIds.forEach(id => {
              if (sourceImages[String(id)]) retrySourceImages[String(id)] = sourceImages[String(id)];
            });
            await api.post('/api/skus/fetch-images', {
              ids: missingSavedIds,
              marketplace: 'auto',
              concurrency: IMAGE_FETCH_CONCURRENCY,
              source_images: retrySourceImages,
            });
            verifiedRows = await refreshSavedChunk(missingSavedIds);
          }
          const stillMissing = missingSavedIds.filter(id => !safeStr(verifiedRows?.get(String(id))?.image_url));
          if (stillMissing.length) {
            totals.failed += stillMissing.length;
            console.warn('Image rows still missing after save verification:', stillMissing);
          }
        } catch (err) {
          failedChunks.push(index + 1);
          totals.failed += chunk.length;
          console.error('Image fetch chunk failed:', err);
        }
      }
      toast.update(toastId, {
        render: `Images fetched ${totals.fetched}, skipped ${totals.skipped}, failed ${totals.failed}, FSN backfilled ${totals.backfilled}${failedChunks.length ? ` (${failedChunks.length} chunk failed)` : ''}`,
        type: totals.failed ? 'warning' : 'success',
        isLoading: false,
        autoClose: 7000,
      });
      setLocalDatasetReady(false);
      await fetchRows({
        scope: filterScope,
        page: 0,
        pageSize,
        sortModel,
        filters: globalFilters,
        forceAll: filterScope === 'local' || showAllRows || hasActiveFilters(globalFilters),
        label: showAllRows ? 'Load all rows' : undefined,
      });
    } catch (e) {
      toast.update(toastId, {
        render: 'Image fetch failed: ' + (e?.response?.data?.error || e.message || 'unknown error'),
        type: 'error',
        isLoading: false,
        autoClose: 7000,
      });
    } finally {
      setLoading(false);
    }
  };

  const disableSelectedSkus = async () => {
    const ids = (selectionModel || []).map(Number).filter(Number.isFinite);
    if (!ids.length) return toast.warn('Select SKU rows to exclude');
    const ok = window.confirm(`Exclude ${ids.length} selected SKU row(s)? They will not appear in SKU tools again until you re-include them from Excluded SKUs.`);
    if (!ok) return;

    setLoading(true);
    try {
      const { data } = await api.patch('/api/skus/disable', { ids });
      toast.success(`Excluded ${data.updated || 0} SKU row(s)`);
      setSelectionModel([]);
      setLocalDatasetReady(false);
      await fetchRows({
        scope: filterScope,
        page: 0,
        pageSize,
        sortModel,
        filters: globalFilters,
        forceAll: filterScope === 'local' || showAllRows || hasActiveFilters(globalFilters),
        label: showAllRows ? 'Load all rows' : undefined,
      });
    } catch (e) {
      toast.error('Exclude failed: ' + (e?.response?.data?.error || e.message || 'unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // bulk fill
  const [bulkField, setBulkField] = useState('color1');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkValueOptions, setBulkValueOptions] = useState([]);
  const [bulkValueLoading, setBulkValueLoading] = useState(false);
  const [bulkUndoAnchor, setBulkUndoAnchor] = useState(null);
  const [bulkUndoHistory, setBulkUndoHistory] = useState([]);
  const [bulkUndoLoading, setBulkUndoLoading] = useState(false);
  const [filterValueOptions, setFilterValueOptions] = useState({});
  const [filterValueLoading, setFilterValueLoading] = useState({});
  const filterValueLoadedRef = useRef({});
  const [breakDialogOpen, setBreakDialogOpen] = useState(false);
  const [breakCriteria, setBreakCriteria] = useState('AUTO');
  const [breakTarget, setBreakTarget] = useState('selected');

  const loadFilterValueOptions = useCallback((field) => {
    if (!field || filterValueLoadedRef.current[field]) return;
    filterValueLoadedRef.current[field] = true;
    if (CLIENT_ONLY_FILTER_FIELDS.has(field)) {
      setFilterValueOptions(prev => ({ ...prev, [field]: [] }));
      return;
    }
    setFilterValueLoading(prev => ({ ...prev, [field]: true }));
    api.get('/api/skus/field-values', {
      params: { field, limit: UNIQUE_VALUE_LIMIT },
    })
      .then(({ data }) => {
        setFilterValueOptions(prev => ({
          ...prev,
          [field]: Array.isArray(data?.values) ? data.values : [],
        }));
      })
      .catch((err) => {
        filterValueLoadedRef.current[field] = false;
        setFilterValueOptions(prev => ({ ...prev, [field]: [] }));
        toast.error(`Failed to load ${fieldLabel(field)} values: ` + (err?.response?.data?.error || err.message || 'unknown'));
      })
      .finally(() => {
        setFilterValueLoading(prev => ({ ...prev, [field]: false }));
      });
  }, []);

  const mergeFilterSelection = useCallback((currentValue, selectedValue) => {
    const selected = safeStr(selectedValue?.value ?? selectedValue).trim();
    if (!selected) return safeStr(currentValue);
    if (selected === '?' || selected === '!?') return selected;

    const current = safeStr(currentValue);
    const hasDelimiter = /[,\uFF0C;|]/.test(current);
    const trailingDelimiter = /[,\uFF0C;|]\s*$/.test(current);
    if (!hasDelimiter) {
      const trimmed = current.trim();
      return trimmed.startsWith('!') && !selected.startsWith('!') ? `!${selected}` : selected;
    }

    const rawParts = current.split(/[,\uFF0C;|]/);
    const kept = (trailingDelimiter ? rawParts : rawParts.slice(0, -1))
      .map(part => part.trim())
      .filter(Boolean);
    const last = trailingDelimiter ? '' : safeStr(rawParts[rawParts.length - 1]).trim();
    const replacement = last.startsWith('!') && !selected.startsWith('!') ? `!${selected}` : selected;
    return [...kept, replacement].join(',');
  }, []);

  const getFilterOptions = useCallback((field, inputValue = '') => {
    const syntax = FILTER_SYNTAX_OPTIONS;
    const values = filterValueOptions[field] || [];
    const needle = safeStr(inputValue)
      .split(/[,\uFF0C;|]/)
      .pop()
      .replace(/^!/, '')
      .trim()
      .toLowerCase();
    return [...syntax, ...values]
      .filter((option, index, arr) => {
        const value = safeStr(option?.value ?? option).trim();
        return value && arr.findIndex(item => safeStr(item?.value ?? item).trim() === value) === index;
      })
      .filter(option => {
        const value = safeStr(option?.value ?? option).toLowerCase();
        if (!needle) return true;
        return value.includes(needle);
      })
      .slice(0, 100);
  }, [filterValueOptions]);

  useEffect(() => {
    let cancelled = false;
    setBulkValueLoading(true);
    api.get('/api/skus/field-values', {
      params: { field: bulkField, limit: UNIQUE_VALUE_LIMIT },
    })
      .then(({ data }) => {
        if (cancelled) return;
        setBulkValueOptions(Array.isArray(data?.values) ? data.values : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setBulkValueOptions([]);
        toast.error('Failed to load values: ' + (err?.response?.data?.error || err.message || 'unknown'));
      })
      .finally(() => {
        if (!cancelled) setBulkValueLoading(false);
      });
    return () => { cancelled = true; };
  }, [bulkField]);

  const openBreakDialog = () => {
    setBreakTarget(selectionModel.length ? 'selected' : 'loaded');
    setBreakDialogOpen(true);
  };

  const breakSampleRow = useMemo(() => {
    if (selectionModel.length) {
      const selected = new Set(selectionModel.map(Number));
      return visibleRows.find(row => selected.has(Number(row.id))) || visibleRows[0] || null;
    }
    return visibleRows[0] || null;
  }, [selectionModel, visibleRows]);

  const breakPreviewParts = useMemo(
    () => splitSkuForBreak(breakSampleRow?.sku || '', breakCriteria),
    [breakSampleRow, breakCriteria]
  );

  const showSplitFields = (count = SPLIT_FIELDS.length) => {
    const activeFields = SPLIT_FIELDS.slice(0, Math.max(1, Math.min(SPLIT_FIELDS.length, count || 1)));
    setColumnVisibilityModel(prev => {
      const next = { ...(prev || {}) };
      activeFields.forEach(field => { next[field] = true; });
      return next;
    });
    setFilterVisibilityModel(prev => {
      const next = { ...(prev || {}) };
      activeFields.forEach(field => { next[field] = true; });
      return next;
    });
    setShowGlobalFilters(true);
  };

  const handleBreakSku = async () => {
    const idsFromRows = (items) => Array.from(new Set((items || []).map(row => Number(row.id)).filter(Number.isFinite)));
    const selectedIds = selectionModel.map(Number).filter(Number.isFinite);
    const loadedIds = idsFromRows(visibleRows);
    const useSelected = breakTarget === 'selected';
    const useLoaded = breakTarget === 'loaded';
    const useFiltered = breakTarget === 'filtered';

    let ids = [];
    let filters = null;
    if (useSelected) ids = selectedIds;
    if (useLoaded) ids = loadedIds;
    if (useFiltered) filters = { ...globalFilters, sku_scope: skuScope || undefined };

    if ((useSelected || useLoaded) && !ids.length) {
      toast.warn(useSelected ? 'Select rows before BREAK' : 'Load rows before BREAK');
      return;
    }
    if (useFiltered && !hasActiveFilters(globalFilters)) {
      toast.warn('Apply a global filter first, or use Loaded grid rows');
      return;
    }

    setLoading(true);
    const targetLabel = useSelected ? `${ids.length} selected row(s)` : useLoaded ? `${ids.length} loaded row(s)` : 'filtered row(s)';
    const toastId = toast.loading(`Breaking SKU for ${targetLabel}...`);
    try {
      const { data } = await api.post('/api/skus/break', {
        sourceField: 'sku',
        criteria: breakCriteria,
        ...(ids.length ? { ids } : { filters }),
      });

      const maxColumns = Number(data?.max_columns || breakPreviewParts.length || 1);
      showSplitFields(maxColumns);

      if (ids.length) {
        const targetIds = new Set(ids);
        setRows(prev => prev.map(row => (
          targetIds.has(Number(row.id))
            ? { ...row, ...splitPartsToRowPatch(splitSkuForBreak(row.sku, breakCriteria)) }
            : row
        )));
      } else {
        await fetchRows({
          scope: filterScope,
          page: 0,
          pageSize,
          sortModel,
          filters: globalFilters,
          forceAll: filterScope === 'local' || showAllRows || hasActiveFilters(globalFilters),
          label: showAllRows ? 'Load all rows' : undefined,
        });
      }

      toast.update(toastId, {
        render: `Broke ${data?.updated ?? 0} row(s) into ${maxColumns} column(s)`,
        type: 'success',
        isLoading: false,
        autoClose: 4000,
      });
      setBreakDialogOpen(false);
    } catch (e) {
      toast.update(toastId, {
        render: 'Break failed: ' + (e?.response?.data?.error || e.message || 'unknown error'),
        type: 'error',
        isLoading: false,
        autoClose: 6000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkFill = async () => {
  const value = bulkValue.trim();
  const clearValue = !value;
  if (clearValue) {
    const targetDescription = selectAllFiltered
      ? 'all rows matching the current filters'
      : `${selectionModel.length} selected row(s)`;
    const confirmed = window.confirm(
      `No value was entered. Clear ${fieldLabel(bulkField)} for ${targetDescription}?\n\nThis action will save blank values and can be undone from Undo Fill.`
    );
    if (!confirmed) return;
  }

  setLoading(true);
  const toastId = toast.loading(clearValue ? 'Clearing values…' : 'Filling values…');
  const t0 = performance.now();

  try {
    // 1) decide targets as you already do
    let targetIds = new Set();

    if (selectAllFiltered) {
      const scopeObj = parseSkuScope(skuScope);
      const filtered = applyClientFilters(rows, globalFilters, scopeObj);
      targetIds = new Set(filtered.map(r => r.id));
      if (!targetIds.size) {
        toast.update(toastId, { render: 'No rows match current filters.', type: 'info', isLoading: false, autoClose: 3000 });
        setLoading(false);
        return;
      }
      // 2A) WRITE TO DB for all matching (server will apply the same filters)
      await api.patch('/api/skus/bulk-update', {
        field: bulkField,
        value: clearValue ? null : value,
        filters: { ...globalFilters, sku_scope: skuScope || undefined }
      });
    } else {
      if (!selectionModel.length) {
        toast.update(toastId, { render: 'Select rows or enable "Select All (filtered)"', type: 'info', isLoading: false, autoClose: 3000 });
        setLoading(false);
        return;
      }
      // 2B) WRITE TO DB for selected IDs (chunking optional)
      const ids = selectionModel.map(Number).filter(Number.isFinite);
      await api.patch('/api/skus/bulk-update', {
        ids,
        field: bulkField,
        value: clearValue ? null : value
      });
      targetIds = new Set(ids);
    }

    // 3) optimistic local update so UI reflects the change without refetching
    let updated = 0;
    setRows(prev => prev.map(r => {
      if (!targetIds.has(r.id)) return r;
      updated += 1;
      const nextRow = { ...r, [bulkField]: clearValue ? null : value };
      if (MSKU_COMPONENT_FIELDS.includes(bulkField)) {
        nextRow.m_sku_text = buildClientMSkuText(nextRow);
      }
      return nextRow;
    }));
    setSelectionModel([]);

    const ms = (performance.now() - t0).toFixed(0);
    toast.update(toastId, {
      render: `${clearValue ? 'Cleared' : 'Filled'} ${updated} row(s) in ${ms} ms (saved to DB)`,
      type: 'success',
      isLoading: false,
      autoClose: 4000
    });
  } catch (e) {
    toast.update(toastId, {
      render: 'Update failed: ' + (e?.response?.data?.error || e.message || 'unknown error'),
      type: 'error',
      isLoading: false,
      autoClose: 5000
    });
  }
 finally {
    setLoading(false);
  }
};

  const loadBulkUndoHistory = useCallback(async () => {
    setBulkUndoLoading(true);
    try {
      const { data } = await api.get('/api/skus/bulk-update-batches', {
        params: { limit: 50 },
      });
      setBulkUndoHistory(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      setBulkUndoHistory([]);
      toast.error('Failed to load undo history: ' + (e?.response?.data?.error || e.message || 'unknown error'));
    } finally {
      setBulkUndoLoading(false);
    }
  }, []);

  const openBulkUndoMenu = async (event) => {
    setBulkUndoAnchor(event.currentTarget);
    await loadBulkUndoHistory();
  };

  const closeBulkUndoMenu = () => setBulkUndoAnchor(null);

  const handleUndoBulkFill = async (batch) => {
    if (!batch?.id) return;
    const label = `${fieldLabel(batch.field_name)} = ${safeStr(batch.value_text) || '(blank)'}`;
    const count = Number(batch.item_count || batch.target_count || batch.changed_count || 0);
    if (!window.confirm(`Undo this bulk fill?\n\n${label}\nRows: ${count}`)) return;

    setLoading(true);
    const toastId = toast.loading('Undoing bulk fill...');
    try {
      const { data } = await api.post(`/api/skus/bulk-update-batches/${batch.id}/undo`);
      toast.update(toastId, {
        render: `Undid ${data?.undone || 0} row(s) from bulk fill #${batch.id}`,
        type: 'success',
        isLoading: false,
        autoClose: 5000,
      });
      closeBulkUndoMenu();
      setLocalDatasetReady(false);
      await fetchRows({
        scope: filterScope,
        page: 0,
        pageSize,
        sortModel,
        filters: globalFilters,
        forceAll: filterScope === 'local' || showAllRows || hasActiveFilters(globalFilters),
        label: showAllRows ? 'Load all rows' : undefined,
      });
    } catch (e) {
      toast.update(toastId, {
        render: 'Undo failed: ' + (e?.response?.data?.error || e.message || 'unknown error'),
        type: 'error',
        isLoading: false,
        autoClose: 7000,
      });
    } finally {
      setLoading(false);
      await loadBulkUndoHistory();
    }
  };



  // csv utils
  const downloadSampleCsv = () => {
    const csv = Papa.unparse([SAMPLE_HEADERS], { header: false });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'sku_sample_headers_only.csv'; a.click(); URL.revokeObjectURL(a.href);
    toast.success('Sample CSV downloaded');
  };
  // REPLACE your existing downloadSelectedCsv with this:
  const downloadSelectedCsv = () => {
    if (!selectionModel.length) return toast.info('Select some rows first');

    // Minimal Excel-safe helper (keeps =, +, -, @ as text)
    const excelSafe = (v) => {
      const s = v == null ? '' : String(v);
      return /^[=+\-@]/.test(s) ? `\t${s}` : s;
    };

    const idSet = new Set(selectionModel.map(Number));
    const ordered = ['sku','fsn','image_url','variant_count','stock_quantity','real_skus','account_name','brand','style','cmb','frt_bck','print1','color1','style2','frt_bck1','print2','color2','size', ...SPLIT_FIELDS, 'm_sku_text', 'remarks'];

    const data = rows
      .filter(r => idSet.has(r.id))
      .map(r => {
        const o = {};
        ordered.forEach(f => { o[f] = excelSafe(r?.[f] ?? ''); });
        return o;
      });

    // BOM so Excel opens UTF-8 correctly
    const csv = '\ufeff' + Papa.unparse(data, { columns: ordered });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `sku_selected_${data.length}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Downloaded ${data.length} selected row(s)`);
  };

  const downloadHierarchyExcel = async () => {
    if (loading) return;
    const exportFilters = {
      ...(globalFilters || {}),
      ...(filterScope === 'global' ? (localHeaderFilters || {}) : {}),
      sku_scope: skuScope || undefined,
    };
    const hasLocalOnlyFilter = Boolean(
      safeStr(localQuery).trim()
      || Object.values(localHeaderFilters || {}).some(value => safeStr(value).trim())
    );
    // Local mode normally contains the complete master dataset, so do not
    // serialize hundreds of thousands of IDs into the browser request. Only
    // pass exact IDs when a browser-only quick/header filter needs them.
    const ids = filterScope === 'local' && hasLocalOnlyFilter
      ? visibleRows.map(row => Number(row?.id)).filter(Number.isFinite)
      : [];
    if (filterScope === 'local' && hasLocalOnlyFilter && !ids.length) {
      toast.info('No SKU rows match the current Local filters');
      return;
    }

    setLoading(true);
    try {
      let response = await api.post('/api/skus/hierarchy-export', {
        filters: exportFilters,
        ids,
      }, { responseType: 'blob', validateStatus: status => status >= 200 && status < 300 });

      // Large exports are queued by the backend so they are not killed by a
      // browser/Cloudflare request timeout. Small exact-ID exports download directly.
      const contentType = safeStr(response.headers?.['content-type']).toLowerCase();
      if (response.status === 202 || contentType.includes('application/json')) {
        const jobInfo = JSON.parse(await response.data.text());
        const jobId = jobInfo?.job_id;
        if (!jobId) throw new Error(jobInfo?.error || 'Export job was not created');
        toast.info('Preparing hierarchy Excel in the background…');
        for (let attempt = 0; attempt < 180; attempt += 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          response = await api.get(`/api/skus/hierarchy-export/${encodeURIComponent(jobId)}`, {
            responseType: 'blob',
            validateStatus: status => status >= 200 && status < 300,
          });
          const pollType = safeStr(response.headers?.['content-type']).toLowerCase();
          if (response.status === 202 || pollType.includes('application/json')) continue;
          break;
        }
        const finalType = safeStr(response.headers?.['content-type']).toLowerCase();
        if (finalType.includes('application/json')) {
          const jobError = JSON.parse(await response.data.text());
          throw new Error(jobError?.error || 'Hierarchy export did not finish');
        }
      }

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `sku_hierarchy_${filterScope}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success('SKU hierarchy Excel downloaded');
    } catch (error) {
      let message = error?.response?.data?.error || error?.message || 'SKU hierarchy export failed';
      if (error?.response?.data instanceof Blob) {
        try {
          const parsed = JSON.parse(await error.response.data.text());
          message = parsed?.error || message;
        } catch {}
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const autoSizeColumns = () => {
    if (!rows.length) return toast.info('No rows to measure');
    const next = { ...columnWidths };
    columns.forEach((col) => {
      const values = rows.map(r => r[col.field]);
      next[col.field] = estimateWidth(col.headerName ?? col.field, values);
    });
    setColumnWidths(next);
    toast.success('Columns autosized');
  };

  const activeHeaderField = headerMenu?.field || '';
  const activeHeaderTitle = headerMenu?.title || fieldLabel(activeHeaderField);
  const activeHeaderCheckedSet = useMemo(
    () => new Set(splitCSV(activeHeaderField ? localHeaderFilters[activeHeaderField] : '')),
    [activeHeaderField, localHeaderFilters]
  );
  const activeHeaderVisible = activeHeaderField ? columnVisibilityModel?.[activeHeaderField] !== false : false;
  const isImageHeaderMenu = activeHeaderField === 'image_url';
  const imageViewCanDecrease = imageViewSize > IMAGE_VIEW_MIN;
  const imageViewCanIncrease = imageViewSize < IMAGE_VIEW_MAX;

  const applyHeaderSort = (direction) => {
    if (!activeHeaderField) return;
    setSortModel(direction ? [{ field: activeHeaderField, sort: direction }] : []);
    closeHeaderMenu();
  };

  const toggleActiveHeaderColumnVisibility = () => {
    if (!activeHeaderField) return;
    setColumnVisibilityModel(prev => {
      const currentlyVisible = prev?.[activeHeaderField] !== false;
      return { ...(prev || {}), [activeHeaderField]: !currentlyVisible };
    });
  };

  const toggleActiveHeaderOption = (opt) => {
    if (!activeHeaderField) return;
    const cur = new Set(splitCSV(localHeaderFilters[activeHeaderField] || ''));
    if (cur.has(opt)) cur.delete(opt); else cur.add(opt);
    setLocalHeaderFilters(prev => ({ ...prev, [activeHeaderField]: Array.from(cur).join(',') }));
  };

  const selectAllActiveHeaderOptions = () => {
    if (!activeHeaderField) return;
    setLocalHeaderFilters(prev => ({
      ...prev,
      [activeHeaderField]: headerMenuOptions.slice(0, UNIQUE_VALUE_LIMIT).join(',')
    }));
  };

  const clearActiveHeaderOptions = () => {
    if (!activeHeaderField) return;
    setLocalHeaderFilters(prev => ({ ...prev, [activeHeaderField]: '' }));
  };

  const handleHeaderMenuClose = (_event, reason) => {
    if (reason === 'backdropClick' && Date.now() - headerMenuOpenedAtRef.current < 250) return;
    closeHeaderMenu();
  };

  const selectedUploadAccount = uploadAccounts.find(account => account?.name === uploadAccountName) || null;
  const uploadKindLabel = uploadKind
    ? (uploadKind === 'flipkart_listing' ? 'Flipkart seller listing export' : uploadKind === 'flipkart_inventory' ? 'Flipkart inventory' : 'SKU master')
    : (file ? 'Server will detect' : 'No file selected');

  const handleUploadFromDialog = async () => {
    const ok = await uploadCsv();
    if (ok) setUploadDialogOpen(false);
  };

  // ----- Render -----
  return (
    <Stack spacing={2} sx={{ p: 2 }}>
             <Card>
              <CardContent>
                <Typography variant="h5">SKU Upload & Master</Typography>
                {/* Hidden file input */}
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={onFileChange} />

                {/* TOP ROW: Upload menu + Bulk fill + Density + small actions + preview + local-header-filter chip */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>

                  {/* Upload menu */}
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<UploadFileIcon />}
                    onClick={() => setUploadDialogOpen(true)}
                  >
                    Upload
                  </Button>

                  <Button
                    size="small"
                    variant="contained"
                    color="info"
                    startIcon={<CloudDownloadIcon />}
                    onClick={() => setImportDialogOpen(true)}
                    disabled={loading}
                  >
                    IMPORT
                  </Button>

                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<ImageSearchIcon />}
                    onClick={fetchSelectedImages}
                    disabled={loading || selectionModel.length === 0}
                  >
                    FETCH IMAGE
                  </Button>

                  {/* Small actions */}
                  <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={downloadSelectedCsv}>
                    Selected
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<DownloadIcon />}
                    onClick={downloadHierarchyExcel}
                    disabled={loading || (filterScope === 'local' && visibleRows.length === 0)}
                  >
                    HIERARCHY EXCEL
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    startIcon={<ColorLensIcon />}
                    onClick={() => navigate('/admin/sku-color-audit', {
                      state: {
                        mode: selectionModel.length ? 'selected' : 'filtered',
                        ids: (selectionModel || []).map(Number).filter(Number.isFinite),
                        filterSummary: {
                          ...(globalFilters || {}),
                          ...(localHeaderFilters || {}),
                          sku_scope: skuScope || '',
                        },
                        queryParams: {
                          ...(globalFilters || {}),
                          ...(localHeaderFilters || {}),
                          sku_scope: skuScope || undefined,
                          sort_by: sortModel?.[0]?.field || 'id',
                          sort_dir: sortModel?.[0]?.sort || 'desc',
                        },
                        sortModel,
                      },
                    })}
                  >
                    COLOR AUDIT
                  </Button>

                  <Tooltip title={selectionModel.length === 0 ? 'Select SKU rows first' : 'Hide selected SKUs from SKU tools'}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<BlockIcon />}
                        onClick={disableSelectedSkus}
                        disabled={loading || selectionModel.length === 0}
                      >
                        Exclude Selected ({selectionModel.length})
                      </Button>
                    </span>
                  </Tooltip>

                  <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    startIcon={<BlockIcon />}
                    onClick={() => navigate('/admin/excluded-skus')}
                  >
                    Open Excluded Page
                  </Button>

                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    onClick={openBreakDialog}
                    disabled={loading || !visibleRows.length}
                  >
                    BREAK
                  </Button>

                  <Tooltip title="Autosize columns">
                    <IconButton size="small" onClick={autoSizeColumns} aria-label="Autosize">
                      <AutoFixHighIcon />
                    </IconButton>
                  </Tooltip>

                  <Button size="small" variant="outlined" startIcon={<FilterListIcon />} onClick={openFilterMenu}>
                    Fields
                  </Button>

                  <FormControlLabel
                    control={<Switch checked={showGlobalFilters} onChange={(e) => setShowGlobalFilters(e.target.checked)} size="small" />}
                    label="Filters"
                    sx={{ ml: 1 }}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={showAllRows}
                        onChange={async (e) => {
                          const checked = e.target.checked;
                          setShowAllRows(checked);
                          setPage(0);
                          setSelectionModel([]);
                          if (filterScope === 'global') {
                            await fetchRows({
                              scope: 'global',
                              page: 0,
                              pageSize,
                              sortModel,
                              filters: globalFilters,
                              forceAll: checked || hasActiveFilters(globalFilters),
                              label: checked ? 'Load all rows' : undefined,
                            });
                          }
                        }}
                        size="small"
                      />
                    }
                    label="All rows"
                  />

                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={filterScope}
                    onChange={onScopeChange}
                    aria-label="Filter scope"
                  >
                    <ToggleButton value="global" aria-label="Global scope">Global</ToggleButton>
                    <ToggleButton value="local" aria-label="Local scope">Local</ToggleButton>
                  </ToggleButtonGroup>

                  <Tooltip
                    arrow
                    placement="bottom-start"
                    title={
                      <Box sx={{ fontSize: 12, lineHeight: 1.6 }}>
                        <div><strong>Filter syntax</strong></div>
                        <div><code>?</code> blank rows</div>
                        <div><code>!?</code> non-blank rows</div>
                        <div><code>BLUE</code> include BLUE</div>
                        <div><code>=GREEN</code> exact GREEN only</div>
                        <div><code>!RED</code> exclude RED</div>
                        <div><code>!=GREEN</code> exclude exact GREEN only</div>
                        <div><code>RED,BLUE</code> include RED or BLUE</div>
                        <div><code>!RED,BLUE</code> include BLUE and exclude RED</div>
                        <div><code>!RED,!BLACK</code> exclude both</div>
                      </Box>
                    }
                  >
                    <IconButton size="small" aria-label="Filter syntax help">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* Bulk fill controls moved into top row */}
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="bulk-col-label">Column</InputLabel>
                    <Select
                      labelId="bulk-col-label"
                      id="bulk-col"
                      label="Column"
                      value={bulkField}
                      onChange={(e) => setBulkField(e.target.value)}
                    >
                      <MenuItem value="sku">SKU</MenuItem>
                      <MenuItem value="fsn">FSN</MenuItem>
                      <MenuItem value="account_name">A/C</MenuItem>
                      <MenuItem value="brand">BRAND</MenuItem>
                      <MenuItem value="style">STYLE</MenuItem>
                      <MenuItem value="cmb">CMB</MenuItem>
                      <MenuItem value="frt_bck">FRT/BCK</MenuItem>
                      <MenuItem value="print1">PRINT</MenuItem>
                      <MenuItem value="color1">COLOR</MenuItem>
                      <MenuItem value="style2">STYLE2</MenuItem>
                      <MenuItem value="frt_bck1">FRT/BCK1</MenuItem>
                      <MenuItem value="print2">PRINT2</MenuItem>
                      <MenuItem value="color2">COLOR2</MenuItem>
                      <MenuItem value="size">SIZE</MenuItem>
                                {SPLIT_FIELDS.map(f => <MenuItem key={f} value={f}>{fieldLabel(f)}</MenuItem>)}
                    </Select>
                  </FormControl>

                  <Autocomplete
                    freeSolo
                    size="small"
                    options={bulkValueOptions}
                    loading={bulkValueLoading}
                    inputValue={bulkValue}
                    onInputChange={(_event, value) => setBulkValue(value || '')}
                    onChange={(_event, value) => setBulkValue(safeStr(value))}
                    sx={{ minWidth: 240 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Value"
                        placeholder="Leave blank to clear (confirmation required)"
                        onKeyDown={e => { if (e.key === 'Enter') handleBulkFill(); }}
                      />
                    )}
                  />

                  <Button size="small" variant="contained" onClick={handleBulkFill}>Fill</Button>

                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<UndoIcon />}
                    onClick={openBulkUndoMenu}
                    disabled={loading || bulkUndoLoading}
                  >
                    Undo Fill
                  </Button>

                  <Menu
                    anchorEl={bulkUndoAnchor}
                    open={Boolean(bulkUndoAnchor)}
                    onClose={closeBulkUndoMenu}
                    keepMounted
                  >
                    <MenuItem disabled>
                      <ListItemText
                        primary="Latest 50 fills"
                        secondary={bulkUndoLoading ? 'Loading...' : 'Choose one to undo'}
                      />
                    </MenuItem>
                    <Divider />
                    {!bulkUndoLoading && !bulkUndoHistory.length && (
                      <MenuItem disabled>
                        <ListItemText primary="No undoable fills" />
                      </MenuItem>
                    )}
                    {bulkUndoHistory.map(batch => (
                      <MenuItem key={batch.id} onClick={() => handleUndoBulkFill(batch)}>
                        <ListItemIcon><UndoIcon fontSize="small" /></ListItemIcon>
                        <ListItemText
                          primary={`#${batch.id} ${fieldLabel(batch.field_name)}: ${safeStr(batch.value_text) || '(blank)'}`}
                          secondary={`${batch.item_count || batch.target_count || 0} row(s) | ${batch.created_at || ''}`}
                        />
                      </MenuItem>
                    ))}
                  </Menu>

                </Box>

                <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
                  <DialogTitle>Upload SKU File</DialogTitle>
                  <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel id="sku-upload-group-label">Group</InputLabel>
                        <Select
                          labelId="sku-upload-group-label"
                          label="Group"
                          value={uploadGroup}
                          onChange={(event) => setUploadGroup(event.target.value)}
                        >
                          {uploadAccountGroups.map(group => (
                            <MenuItem key={group} value={group}>{group}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl size="small" fullWidth>
                        <InputLabel id="sku-upload-account-dialog-label">Account / Subgroup</InputLabel>
                        <Select
                          labelId="sku-upload-account-dialog-label"
                          label="Account / Subgroup"
                          value={uploadAccountName}
                          onChange={(event) => setUploadAccountName(event.target.value)}
                          disabled={!uploadGroup}
                        >
                          <MenuItem value=""><em>Select account</em></MenuItem>
                          {uploadAccountOptions.map(account => (
                            <MenuItem key={account.id || account.name} value={account.name}>
                              {accountDisplayName(account)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1.25 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {file?.name || 'No file selected'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {uploadKindLabel}
                              {selectedUploadAccount ? ` | ${selectedUploadAccount.name}` : ''}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                            onClick={() => fileRef?.current?.click()}
                            disabled={!uploadAccountName}
                            sx={{ flex: '0 0 auto' }}
                          >
                            Choose
                          </Button>
                        </Stack>
                      </Box>

                      {preview.length > 0 && (
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          label={`Preview rows: ${preview.length}`}
                          sx={{ alignSelf: 'flex-start' }}
                        />
                      )}
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={downloadSampleCsv} startIcon={<DownloadIcon />}>Sample</Button>
                    <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
                    <Button
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                      onClick={handleUploadFromDialog}
                      disabled={loading || !uploadAccountName || !file}
                    >
                      Upload
                    </Button>
                  </DialogActions>
                </Dialog>

                <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="xs" fullWidth>
                  <DialogTitle>Import SKUs From Orders</DialogTitle>
                  <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                      This will import missing SKU bases from uploaded order data into SKU Master.
                    </Typography>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
                    <Button
                      variant="contained"
                      color="info"
                      startIcon={<CloudDownloadIcon />}
                      disabled={loading}
                      onClick={async () => {
                        setImportDialogOpen(false);
                        await importOrderSkus();
                      }}
                    >
                      Import
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Field and filter visibility menu */}
                <Menu anchorEl={filterMenuAnchor} open={Boolean(filterMenuAnchor)} onClose={closeFilterMenu} keepMounted>
                  <MenuItem onClick={selectAllFields}>
                    <ListItemIcon><CheckBoxIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Select all" secondary="Show all filters and columns" />
                  </MenuItem>
                  <MenuItem onClick={deselectAllFields}>
                    <ListItemIcon><CheckBoxOutlineBlankIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Deselect all" secondary="Hide all filters and columns" />
                  </MenuItem>
                  <Divider />
                  {FILTER_KEYS.map((k) => {
                    const checked = filterVisibilityModel?.[k] !== false && columnVisibilityModel?.[k] !== false;
                    return (
                      <MenuItem key={k} onClick={() => toggleFieldVisibility(k)}>
                        <ListItemIcon>
                          {checked ? <CheckBoxIcon fontSize="small" /> : <CheckBoxOutlineBlankIcon fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText>{fieldLabel(k)}</ListItemText>
                      </MenuItem>
                    );
                  })}
                </Menu>

                <Dialog open={breakDialogOpen} onClose={() => setBreakDialogOpen(false)} maxWidth="sm" fullWidth>
                  <DialogTitle>Break SKU</DialogTitle>
                  <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                      <TextField
                        size="small"
                        label="Criteria"
                        value={breakCriteria}
                        onChange={(e) => setBreakCriteria(e.target.value)}
                        helperText="AUTO uses fixed columns: A base, B only CMB/COMBO, C first bracket/pair, D second bracket/pair. Missing B stays blank. Re-break clears old split columns first."
                      />

                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>Target rows</Typography>
                        <RadioGroup value={breakTarget} onChange={(e) => setBreakTarget(e.target.value)}>
                          <FormControlLabel
                            value="selected"
                            control={<Radio size="small" />}
                            disabled={!selectionModel.length}
                            label={`Selected rows (${selectionModel.length})`}
                          />
                          <FormControlLabel
                            value="loaded"
                            control={<Radio size="small" />}
                            disabled={!visibleRows.length}
                            label={`Loaded grid rows (${visibleRows.length})`}
                          />
                          <FormControlLabel
                            value="filtered"
                            control={<Radio size="small" />}
                            disabled={filterScope !== 'global' || !hasActiveFilters(globalFilters)}
                            label="Current global filtered rows"
                          />
                        </RadioGroup>
                      </Box>

                      <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1.25 }}>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>Preview</Typography>
                        <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-word' }}>
                          {breakSampleRow?.sku || 'No SKU loaded'}
                        </Typography>
                        <Stack spacing={0.5}>
                          {breakPreviewParts.length ? breakPreviewParts.map((part, index) => (
                            <Typography key={`${part}-${index}`} variant="body2">
                              {SPLIT_FIELDS[index]?.toUpperCase() || index + 1}: {part}
                            </Typography>
                          )) : (
                            <Typography variant="body2" color="text.secondary">No parts found</Typography>
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setBreakDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleBreakSku} disabled={loading}>
                      Break
                    </Button>
                  </DialogActions>
                </Dialog>

                <Menu
                  open={Boolean(headerMenu)}
                  onClose={handleHeaderMenuClose}
                  anchorReference="anchorPosition"
                  anchorPosition={headerMenu?.anchorPosition || { top: 0, left: 0 }}
                  keepMounted
                  disableAutoFocusItem
                  disableRestoreFocus
                  MenuListProps={{
                    onMouseDown: (e) => e.stopPropagation(),
                    onClick: (e) => e.stopPropagation(),
                    sx: { maxHeight: 440, minWidth: 260, fontSize: 13 }
                  }}
                  PaperProps={{
                    onMouseDown: (e) => e.stopPropagation(),
                    onClick: (e) => e.stopPropagation(),
                  }}
                >
                  <MenuItem disabled>
                    <ListItemText primary={activeHeaderTitle} primaryTypographyProps={{ fontSize: 12, fontWeight: 700 }} />
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); applyHeaderSort('asc'); }}>
                    <ListItemText primary="Sort ascending" />
                  </MenuItem>
                  <MenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); applyHeaderSort('desc'); }}>
                    <ListItemText primary="Sort descending" />
                  </MenuItem>
                  <MenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); applyHeaderSort(null); }}>
                    <ListItemText primary="Clear sort" />
                  </MenuItem>

                  <Divider />

                  <MenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleActiveHeaderColumnVisibility(); }}>
                    <Checkbox size="small" checked={activeHeaderVisible} sx={{ mr: 1 }} />
                    <ListItemText primary="Visible" />
                  </MenuItem>

                  {isImageHeaderMenu && (
                    <>
                      <Divider />
                      <Box
                        sx={{ px: 2, py: 1.25, width: 300 }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>Image size</Typography>
                          <Chip size="small" label={`${imageViewSize}px`} />
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Tooltip title="Decrease image size">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => applyImageViewSize(imageViewSize - IMAGE_VIEW_STEP)}
                                disabled={!imageViewCanDecrease}
                                aria-label="Decrease image size"
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Slider
                            size="small"
                            value={imageViewSize}
                            min={IMAGE_VIEW_MIN}
                            max={IMAGE_VIEW_MAX}
                            step={IMAGE_VIEW_STEP}
                            onChange={(_event, value) => applyImageViewSize(value)}
                            aria-label="Image size"
                            sx={{ mx: 1 }}
                          />
                          <Tooltip title="Increase image size">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => applyImageViewSize(imageViewSize + IMAGE_VIEW_STEP)}
                                disabled={!imageViewCanIncrease}
                                aria-label="Increase image size"
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                        <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
                          {IMAGE_VIEW_PRESETS.map(size => (
                            <Button
                              key={size}
                              size="small"
                              variant={imageViewSize === size ? 'contained' : 'outlined'}
                              onClick={() => applyImageViewSize(size)}
                              sx={{ minWidth: 52 }}
                            >
                              {size}
                            </Button>
                          ))}
                        </Stack>
                      </Box>
                    </>
                  )}

                  <Divider />

                  <MenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); selectAllActiveHeaderOptions(); }}>
                    Select all values
                  </MenuItem>
                  <MenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); clearActiveHeaderOptions(); }}>
                    Clear values
                  </MenuItem>

                  <Divider />

                  {headerMenuOptions.length === 0 && (<MenuItem disabled><ListItemText primary="No values" /></MenuItem>)}
                  {headerMenuOptions.map((opt) => (
                    <MenuItem
                      key={opt}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleActiveHeaderOption(opt);
                      }}
                    >
                      <Checkbox size="small" checked={activeHeaderCheckedSet.has(opt)} sx={{ mr: 1 }} />
                      <ListItemText primary={opt} primaryTypographyProps={{ fontSize: 13 }} />
                    </MenuItem>
                  ))}
                </Menu>

                <Divider sx={{ my: 1 }} />

                {/* THIRD ROW: status chips (Selected chip remains here) */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                  <Chip label={`Scope: ${filterScope.toUpperCase()}`} color={filterScope === 'global' ? 'primary' : 'default'} size="small" variant="outlined" />
                  {isClientLocal ? <Chip label="Client (Local)" size="small" /> : (isFullGridMode ? <Chip label="All rows" color="success" size="small" /> : <Chip label="Paginated" size="small" />)}
                  <Chip label={`Grid rows: ${visibleRows.length}`} size="small" />
                  {filterScope === 'global' && !isFullGridMode && <Chip label={`Page rows: ${rows.length}`} size="small" variant="outlined" />}
                  {!isFullGridMode && <Chip label={`Total: ${rowCount}`} size="small" />}
                  <Chip label={`Selected: ${selectionModel.length}`} size="small" color={selectionModel.length ? 'secondary' : 'default'} variant={selectionModel.length ? 'filled' : 'outlined'} />
                  {/* Preview + the new local-header-filter count chip on the far right */}
                  <Chip label={`Header-filtered: ${localHeaderFilteredCount}`} size="small" color={selectionModel.length ? 'secondary' : 'default'} variant={localHeaderFilteredCount ? 'filled' : 'outlined'} />
                </Box>

                {/* GLOBAL filters — compact single-line, wraps to second line when needed */}
                {showGlobalFilters && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      flexWrap: 'wrap',   // allow wrapping to next line when horizontal space is insufficient
                      mb: 1
                    }}
                  >
                    {GRID_FIELD_KEYS.map(k => {
                      if (!filterVisibilityModel[k]) return null;

                      if (k === 'sku') {
                        return (
                          <Box
                            key="sku-combined"
                            sx={{
                              display: 'flex',
                              gap: 1,
                              alignItems: 'center',
                              flex: '0 1 auto'    // try to keep on one line but allow shrinking/wrapping
                            }}
                          >
                            <Autocomplete
                              freeSolo
                              openOnFocus
                              forcePopupIcon
                              size="small"
                              options={getFilterOptions('sku', globalFilters.sku)}
                              filterOptions={(options) => options}
                              loading={!!filterValueLoading.sku}
                              inputValue={safeStr(globalFilters.sku)}
                              onOpen={() => loadFilterValueOptions('sku')}
                              onInputChange={(_event, value, reason) => {
                                if (reason === 'input' || reason === 'clear') {
                                  setGlobalFilters(p => ({ ...p, sku: value || '' }));
                                }
                              }}
                              onChange={(_event, value) => {
                                if (value !== null) {
                                  setGlobalFilters(p => ({ ...p, sku: mergeFilterSelection(p.sku, value) }));
                                }
                              }}
                              getOptionLabel={(option) => safeStr(option?.value ?? option)}
                              renderOption={(props, option) => (
                                <li {...props} key={`${safeStr(option?.value ?? option)}-${safeStr(option?.description ?? '')}`}>
                                  {option?.description ? (
                                    <ListItemText primary={option.value} secondary={option.description} />
                                  ) : option}
                                </li>
                              )}
                              sx={{ minWidth: 140, maxWidth: 320, flex: '0 1 auto' }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="SKU"
                                  placeholder="term,term"
                                  onFocus={() => loadFilterValueOptions('sku')}
                                />
                              )}
                            />
                            <TextField
                              size="small"
                              label="Scope"
                              value={skuScope}
                              onChange={e => setSkuScope(e.target.value)}
                              placeholder="+3 or -3"
                              sx={{ width: 110, flex: '0 1 auto' }}
                            />
                          </Box>
                        );
                      }

                      // compact textfields for other filters
                      return (
                        <Autocomplete
                          key={k}
                          freeSolo
                          openOnFocus
                          forcePopupIcon
                          size="small"
                          options={getFilterOptions(k, globalFilters[k])}
                          filterOptions={(options) => options}
                          loading={!!filterValueLoading[k]}
                          inputValue={safeStr(globalFilters[k])}
                          onOpen={() => loadFilterValueOptions(k)}
                          onInputChange={(_event, value, reason) => {
                            if (reason === 'input' || reason === 'clear') {
                              setGlobalFilters(p => ({ ...p, [k]: value || '' }));
                            }
                          }}
                          onChange={(_event, value) => {
                            if (value !== null) {
                              setGlobalFilters(p => ({ ...p, [k]: mergeFilterSelection(p[k], value) }));
                            }
                          }}
                          getOptionLabel={(option) => safeStr(option?.value ?? option)}
                          renderOption={(props, option) => (
                            <li {...props} key={`${safeStr(option?.value ?? option)}-${safeStr(option?.description ?? '')}`}>
                              {option?.description ? (
                                <ListItemText primary={option.value} secondary={option.description} />
                              ) : option}
                            </li>
                          )}
                          sx={{
                            minWidth: 110,
                            maxWidth: 220,
                            flex: '0 1 auto'   // let it shrink before wrapping
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={fieldLabel(k)}
                              placeholder="?, or comma list"
                              onFocus={() => loadFilterValueOptions(k)}
                            />
                          )}
                        />
                      );
                    })}

                    <Button size="small" variant="outlined" onClick={applyGlobalFilters} sx={{ flex: '0 0 auto' }}>
                      Search
                    </Button>

                    <Button size="small" variant="text" onClick={clearGlobalFilters} sx={{ flex: '0 0 auto' }}>
                      Reset
                    </Button>

                    <Button
                      size="small"
                      variant="outlined"
                      onClick={async () => {
                        setFilterScope('local');
                        setLocalQuery('');
                        setLocalHeaderFilters({});
                        setPage(0);
                        await fetchRows({
                          scope: 'local',
                          forceAll: true,
                          filters: {},
                          sortModel,
                          label: 'Load all',
                        });
                      }}
                      sx={{ flex: '0 0 auto' }}
                    >
                      LoadAll
                    </Button>
                  </Box>
                )}

                {/* DATA GRID */}
                {filterScope === 'local' ? (
                  <Box
                    sx={{
                      height: 'calc(100vh - 330px)',
                      minHeight: 520,
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      ref={localTopScrollerRef}
                      className="sku-local-top-scrollbar"
                      tabIndex={0}
                      aria-label="Scroll columns"
                      onScroll={handleLocalTopScroll}
                      onKeyDown={(e) => {
                        const step = e.key === 'ArrowRight' || e.key === 'ArrowLeft' ? 160 : 0;
                        let nextLeft = null;
                        if (e.key === 'ArrowRight') nextLeft = e.currentTarget.scrollLeft + step;
                        if (e.key === 'ArrowLeft') nextLeft = e.currentTarget.scrollLeft - step;
                        if (e.key === 'End') nextLeft = e.currentTarget.scrollWidth;
                        if (e.key === 'Home') nextLeft = 0;
                        if (nextLeft === null) return;
                        e.preventDefault();
                        e.currentTarget.scrollLeft = nextLeft;
                        if (localGridScrollerRef.current) localGridScrollerRef.current.scrollLeft = nextLeft;
                      }}
                      sx={{
                        height: 17,
                        minHeight: 17,
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        border: '1px solid #ddd',
                        borderBottom: 0,
                        borderRadius: '4px 4px 0 0',
                        bgcolor: '#fff',
                      }}
                    >
                      <Box sx={{ width: localGridWidth, height: 1 }} />
                    </Box>

                    <Box
                      ref={localGridScrollerRef}
                      className="sku-local-virtual-grid"
                      role="grid"
                      tabIndex={0}
                      aria-rowcount={localGridRows.length}
                      onScroll={handleLocalGridScroll}
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        width: '100%',
                        overflow: 'auto',
                        border: '1px solid #ddd',
                        borderRadius: '0 0 4px 4px',
                        bgcolor: '#fff',
                        outline: 'none',
                        '& mark': { padding: '0 2px', borderRadius: 2, backgroundColor: '#ffee58' },
                      }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          minWidth: localGridWidth,
                          width: localGridWidth,
                          height: LOCAL_HEADER_HEIGHT + (localGridRows.length * localRowHeight),
                        }}
                      >
                        <Box
                          role="row"
                          sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 5,
                            display: 'flex',
                            height: LOCAL_HEADER_HEIGHT,
                            minWidth: localGridWidth,
                            width: localGridWidth,
                            bgcolor: '#fff',
                            borderBottom: '1px solid #ddd',
                            boxShadow: '0 1px 0 rgba(0,0,0,0.04)',
                          }}
                        >
                          <Box
                            role="columnheader"
                            sx={{
                              width: LOCAL_SELECTION_COLUMN_WIDTH,
                              flex: `0 0 ${LOCAL_SELECTION_COLUMN_WIDTH}px`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRight: '1px solid #eee',
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={allLocalRowsSelected}
                              indeterminate={!allLocalRowsSelected && someLocalRowsSelected}
                              onChange={(e) => toggleAllLocalRowsSelection(e.target.checked)}
                              inputProps={{ 'aria-label': 'Select all local rows' }}
                            />
                          </Box>

                          {localGridColumns.map((col) => {
                            const width = Number(columnWidths?.[col.field] ?? col.width ?? col.minWidth ?? 120);
                            return (
                              <Box
                                key={col.field}
                                role="columnheader"
                                sx={{
                                  width,
                                  flex: `0 0 ${width}px`,
                                  minWidth: width,
                                  height: '100%',
                                  borderRight: '1px solid #eee',
                                  overflow: 'visible',
                                  boxSizing: 'border-box',
                                }}
                              >
                                {col.renderHeader ? col.renderHeader({ colDef: col }) : fieldLabel(col.field)}
                              </Box>
                            );
                          })}
                        </Box>

                        {localVirtualRows.map(({ row, index }) => {
                          const rowId = Number(row?.id);
                          const checked = Number.isFinite(rowId) && localSelectedIds.has(rowId);
                          return (
                            <Box
                              key={row?.id ?? index}
                              role="row"
                              aria-rowindex={index + 1}
                              data-row-number={index + 1}
                              sx={{
                                position: 'absolute',
                                top: LOCAL_HEADER_HEIGHT + (index * localRowHeight),
                                left: 0,
                                display: 'flex',
                                minWidth: localGridWidth,
                                width: localGridWidth,
                                height: localRowHeight,
                                bgcolor: index % 2 === 0 ? '#fafafa' : '#f0f0f0',
                                borderBottom: '1px solid #e0e0e0',
                                '&:hover': { filter: 'brightness(0.98)' },
                              }}
                            >
                              <Box
                                role="gridcell"
                                sx={{
                                  width: LOCAL_SELECTION_COLUMN_WIDTH,
                                  flex: `0 0 ${LOCAL_SELECTION_COLUMN_WIDTH}px`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRight: '1px solid #eee',
                                  boxSizing: 'border-box',
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={checked}
                                  onChange={(e) => toggleLocalRowSelection(row?.id, e.target.checked)}
                                  inputProps={{ 'aria-label': `Select row ${index + 1}` }}
                                />
                              </Box>

                              {localGridColumns.map((col) => {
                                const width = Number(columnWidths?.[col.field] ?? col.width ?? col.minWidth ?? 120);
                                const content = col.field === '__row_number'
                                  ? index + 1
                                  : (col.renderCell
                                    ? col.renderCell({ id: row?.id, field: col.field, value: row?.[col.field], row })
                                    : safeStr(row?.[col.field]));
                                return (
                                  <Box
                                    key={`${row?.id ?? index}-${col.field}`}
                                    role="gridcell"
                                    sx={{
                                      width,
                                      flex: `0 0 ${width}px`,
                                      minWidth: width,
                                      display: 'flex',
                                      alignItems: 'center',
                                      px: 1,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      borderRight: '1px solid #eee',
                                      boxSizing: 'border-box',
                                      fontSize: 14,
                                    }}
                                    title={col.field === '__row_number' ? String(index + 1) : safeStr(row?.[col.field])}
                                  >
                                    {content}
                                  </Box>
                                );
                              })}
                            </Box>
                          );
                        })}

                        {!loading && !localGridRows.length && (
                          <Box sx={{ position: 'absolute', top: LOCAL_HEADER_HEIGHT + 32, left: 24, color: 'text.secondary' }}>
                            No rows
                          </Box>
                        )}
                        {loading && (
                          <Box sx={{ position: 'absolute', top: LOCAL_HEADER_HEIGHT + 32, left: 24, color: 'text.secondary' }}>
                            Loading...
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <div style={{ height: 'calc(100vh - 330px)', minHeight: 520, width: '100%' }}>
                    <DataGrid
                      key={`grid-${filterScope}`}
                      rows={visibleRows}
                      columns={columns}
                      columnHeaderHeight={100}
                      rowHeight={gridRowHeight}
                      loading={loading}
                      checkboxSelection
                      disableRowSelectionOnClick
                      getRowId={(r) => r.id}
                      slots={{ toolbar: CustomGridToolbar, columnMenu: NullColumnMenu, columnMenuIcon: NullColumnMenuIcon }}
                      density={density}
                      rowSelectionModel={selectionModel}
                      onRowSelectionModelChange={(newModel) => setSelectionModel(Array.isArray(newModel) ? [...newModel] : Array.from(newModel || []))}
                      filterMode="server"
                      sortingMode="server"
                      sortModel={sortModel}
                      onSortModelChange={(m) => {
                        setSortModel(m);
                        const s = m?.[0];
                        toast.info(`Sort: ${s?.field ?? 'none'} ${s?.sort ?? ''}`);
                        fetchRows({
                          scope: 'global',
                          page: isFullGridMode ? 0 : page,
                          pageSize,
                          sortModel: m,
                          filters: globalFilters,
                          forceAll: isFullGridMode,
                          label: showAllRows ? 'Load all rows' : undefined,
                        });
                      }}
                      pagination={!isFullGridMode}
                      hideFooter={isFullGridMode}
                      paginationMode="server"
                      rowCount={rowCount}
                      paginationModel={{ page, pageSize }}
                      pageSizeOptions={SERVER_PAGE_SIZE_OPTIONS}
                      onPaginationModelChange={(model) => {
                        const nextPageSize = clampServerPageSize(model.pageSize);
                        setPage(model.page);
                        setPageSize(nextPageSize);
                      }}
                      columnVisibilityModel={columnVisibilityModel}
                      onColumnVisibilityModelChange={(m) => setColumnVisibilityModel(m)}
                      onColumnResizeStop={({ colDef, width }) => { if (colDef?.field && width) setColumnWidths((w) => ({ ...w, [colDef.field]: width })); }}
                      onColumnWidthChange={({ colDef, width }) => { if (colDef?.field && width) setColumnWidths((w) => ({ ...w, [colDef.field]: width })); }}
                      sx={{
                        '& .MuiDataGrid-columnSeparator': { display: 'none !important' },
                        '& .MuiDataGrid-columnHeaderMenuIcon': { display: 'none !important', pointerEvents: 'none !important' },
                        '& .MuiDataGrid-menuIcon': { display: 'none !important', pointerEvents: 'none !important' },
                        '& .MuiDataGrid-sortIcon': { display: 'none !important', pointerEvents: 'none !important' },
                        '& .MuiDataGrid-columnHeader .MuiIconButton-root': { display: 'none !important', pointerEvents: 'none !important' },
                        '& .custom-col-menu-btn': { display: 'inline-flex !important', pointerEvents: 'auto !important', alignItems: 'center' },
                        '& .MuiDataGrid-columnHeader': { borderRight: 'none', paddingRight: 0, boxSizing: 'border-box', position: 'relative', overflow: 'visible' },
                        '& .MuiDataGrid-columnHeaderTitleContainer': { overflow: 'visible', width: '100%' },
                        '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                        '& .MuiDataGrid-row.even': { backgroundColor: '#fafafa' },
                        '& .MuiDataGrid-row.odd': { backgroundColor: '#f0f0f0' },
                        '& .MuiDataGrid-row:hover': { filter: 'brightness(0.98)' },
                        '& .MuiDataGrid-cell': { py: 0.5 },
                        '& .MuiDataGrid-virtualScroller': { overscrollBehavior: 'contain' },
                        '& mark': { padding: '0 2px', borderRadius: 2, backgroundColor: '#ffee58' },
                        overflow: 'hidden',
                      }}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

      </Stack>
  );
}
